import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import type {
  AnalysisData,
  CaptureFeedback,
  CaptureSequence,
  CapturedImage,
  Settings,
  OverlayState,
  UnassignedImage,
  Landmark,
  CaptureSettings,
  Model3D,
} from './types';
import { getEffectiveViewSequence, VIEW_SEQUENCE, IDX } from './constants';
import {
  drawIdealGrid,
  drawAP,
  drawLat,
  drawCustomPelvis,
  drawLandmarks,
  drawViewportOrientationLabels,
  getPlumbEdgeControlPoints,
  getSegmentsForView,
  getPoseOverlayLandmarkIndices,
  plumbAnchor,
  type LandmarkHover,
} from './lib/drawing';
import { clientToCanvasBitmapPoint } from './lib/canvas-pointer';
import {
  computePoseDragGrabOffset,
  getPelvisPinAcquireRadiusPx,
  getPelvisPinHoverMagnetRadiusPx,
  getPelvisRedLineGrabHalfWidthPx,
  getPoseLandmarkAcquireRadiusPx,
  getPoseLandmarkHoverMagnetRadiusPx,
  MOBILE_POSE_DRAG_SLOP_PX,
  POSE_LANDMARK_SNAP_TO_CENTER_FRAC,
  POSE_LANDMARK_SNAP_TO_CENTER_FRAC_MOBILE,
} from './lib/pose-drag-grab';
import { closestPointOnSegmentT, getPelvisRedLinePinKeys, getStablePelvisRedLine } from './lib/pelvis-redline';
import { inferPelvisPinsForView } from './lib/pelvis-pin-inference';
import { trackPhotoUploadStart } from '@/lib/analytics/analytics-service';
import { SwitchCameraIcon, TrashIcon, UploadIcon, CameraIcon, SettingsIcon, EyeIcon, EyeOffIcon } from './Icons';
import {
  Target,
  XCircle,
  Maximize2,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ZoomIn,
  ZoomOut,
  Plus,
  Minus,
  Undo2,
  Redo2,
  PinOff,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronDown,
  LayoutList,
  Info,
  X,
  GripVertical,
  GripHorizontal,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { PoseLandmarker } from '@mediapipe/tasks-vision';
import CaptureOverlay from './CaptureOverlay';
import CameraErrorNotice from './CameraErrorNotice';
import { useFullscreenEditorKeyboard } from './lib/use-fullscreen-editor-keyboard';
import CaptureToolbarSecondary, {
  CAPTURE_TOOLBAR_ICON_CLASS,
  CAPTURE_TOOLBAR_TARGET_CLASS,
  type CaptureToolbarAction,
} from './CaptureToolbarSecondary';
import CaptureCountdown from './CaptureCountdown';
import GuidedCaptureWizard from './GuidedCaptureWizard';
import CaptureSettingsPanel from './CaptureSettingsPanel';
import RealtimeOverlayCanvas from './RealtimeOverlayCanvas';
import { useStabilityDetection } from './lib/stability';
import { hasMinimumLateralChainForCapture } from './lib/capture-pose-readiness';
import {
  captureFeedbackEquals,
  deriveRealtimeCaptureFeedback,
  landmarksMeaningfullyChanged,
} from './lib/realtime-detection-sync';
import { playPostureShutterSound } from './lib/capture-sounds';
import { CAPTURE_SETUP_WIZARD_REMINDER } from './lib/capture-setup-standard';
import { DEFAULT_CAPTURE_SETTINGS, loadCaptureSettings, saveCaptureSettings, getCameraConstraints } from './lib/capture-settings';
import { applyImageEnhancements } from './lib/image-enhancement';
import { getEnhancementCSS, getTransformCSS } from './lib/image-transformation';
import PostureClassificationOverlay from './PostureClassificationOverlay';
import { compressImage } from './lib/image-compression';
import { CANVAS_ASPECT_RATIO, SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_PX, SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_DESKTOP_PX, fullWidthMediaHeight, useCanvasSize, useObservedBox } from './lib/canvas-size';
import { FullScreenImageDialog } from '@/components/ui/full-screen-image-dialog';
import AnalysisTiles from './AnalysisTiles';
import type { ClassificationResult } from './ClassificationDetails';
import { useIsMobile } from '@/hooks/use-mobile';
import { usePageHeaderVisibility } from '@/contexts/page-header-visibility-context';
import {
  MOBILE_PAGE_HEADER_COLLAPSED_HEIGHT_PX,
  MOBILE_POSTURE_TOOLBAR_HEIGHT_PX,
  MOBILE_POSTURE_VIEW_TABS_HEIGHT_PX,
} from '@/lib/mobile-immersive-chrome';
import { useDeviceOrientation } from './lib/use-device-orientation';
import { normalizeFloorAngleDeg } from '@/lib/posture-analysis/floor-correction';
import { cn } from '@/lib/utils';
import { usePostureMode } from '@/components/posture-analysis/lib/PostureModeContext';
import { shouldShowPelvisOverlay } from '@/components/posture-analysis/lib/posture-mode-strategy';
import { getLaymanViewLabel } from '@/components/posture-analysis/self-assessment/view-display-labels';
import {
  inlineCaptureCanvasTouchAction,
  shouldUseMobilePoseEditMode,
} from '@/components/posture-analysis/self-assessment/mobile-capture-layout';

export interface CaptureHandlers {
  isCameraActive: boolean;
  isReadyForAnalysis: boolean;
  nextCaptureView: CaptureSequence | undefined;
  handleCaptureClick: () => void;
  handleUploadClick: () => void;
  stopCamera: () => void;
}
import logger, { LogCategory } from '@/lib/logging';

/** Fallback snap radii (image-bitmap px) when a captured-settings value is missing. Practitioner-tunable via Settings. */
const LANDMARK_SNAP_DEFAULT_PX = 20;
const PLUMB_SNAP_DEFAULT_PX = 8;
const clampSnapRadius = (v: number | undefined, fallback: number) =>
  Math.max(1, Math.min(100, typeof v === 'number' && Number.isFinite(v) ? v : fallback));

interface CaptureModuleProps {
  settings: Settings;
  captureSettings: CaptureSettings;
  onCaptureSettingsChange: (settings: CaptureSettings) => void;
  capturedImages: Record<CaptureSequence, CapturedImage | null>;
  currentView: CaptureSequence | null;
  overlayState: OverlayState;
  unassignedImages: UnassignedImage[];
  setUnassignedImages: React.Dispatch<React.SetStateAction<UnassignedImage[]>>;
  onQueueImage: (
    dataUrl: string,
    view: CaptureSequence,
    options?: {
      resetToInitialDetection?: boolean;
      preservePlumbline?: boolean;
      gyroscopeTilt?: { gamma: number; beta: number };
    }
  ) => void;
  onManualPlumbUpdate?: (
    view: CaptureSequence,
    options: {
      manualFloorPoints?: [{ x: number; y: number }, { x: number; y: number }];
      manualCorrectionDeg?: number;
      plControlPoint?: { x: number; y: number };
      resetToAuto?: boolean;
    }
  ) => void;
  onViewSelect: (view: CaptureSequence) => void;
  onReset: () => void;
  onDeleteView: () => void;
  onOverlayToggle: (view: CaptureSequence) => void;
  onRealtimeOverlayToggle: (view: CaptureSequence) => void;
  onSetCustomLandmark: (
    view: CaptureSequence,
    point: { x: number; y: number },
    type: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS'
  ) => void;
  /** Remove ASIS/PSIS markers from all views and opt out of pelvic tilt. */
  onClearAllPelvisPins?: () => void;
  /** Translate both pelvis red-line endpoints together (image space). */
  onTranslatePelvisRedLine?: (view: CaptureSequence, delta: { dx: number; dy: number }) => void;
  /** When provided, pose landmarks become draggable (edit mode). */
  onSetPoseLandmark?: (view: CaptureSequence, index: number, point: { x: number; y: number }) => void;
  onCaptureHandlersReady?: (handlers: CaptureHandlers) => void;
  poseLandmarker?: PoseLandmarker | null;
  captureMode?: 'guided' | 'manual';
  onCaptureModeChange?: (mode: 'guided' | 'manual') => void;
  onShowTips?: () => void;
  onReanalyze?: () => void;
  centerPanelMessage?: string | null;
  classification?: ClassificationResult | null;
  showClassificationOverlay?: boolean;
  onToggleClassificationOverlay?: () => void;
  /** Extra pixels reserved on the right for a side-by-side analysis panel (sm+ breakpoint). */
  rightReservationPx?: number;
  /** Hard cap on inline canvas height (px). Mobile split only — not applied in fullscreen editor. */
  maxCanvasHeightPx?: number;
  /** When false, Posterior (back) view is excluded from capture flow. */
  experimentalFeaturesEnabled?: boolean;
  /** Undo/redo for landmark and pelvis edits while fullscreen is open. */
  fullscreenEditHistory?: {
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
  };
  /** Called once when the user starts an edit gesture (pointer down on draggable target or tap placement). Parent should snapshot state for undo. */
  onEditGestureStart?: () => void;
  /** Fullscreen: whether the metrics / View Report column is visible (parent app setting). */
  analysisPanelEnabled?: boolean;
  /** Fullscreen: toggle metrics dashboard (View Report panel). */
  onToggleAnalysisPanel?: () => void;
  /** When true, show fullscreen control to toggle the metrics panel (e.g. when analysis tiles are available). */
  showAnalysisDashboardToggle?: boolean;
  /** Metrics / View Report data (shown inside fullscreen when analysis panel is on). */
  analysisData?: AnalysisData;
  model3D?: Model3D | null;
  /** Self-assessment mobile: edge-to-edge fullscreen dialog (100dvh). */
  immersiveFullscreen?: boolean;
  /** Desktop practitioner: grow the photo to at least half the workspace. */
  fillAvailable?: boolean;
}

const CaptureModule: React.FC<CaptureModuleProps> = ({
  settings, captureSettings, onCaptureSettingsChange, capturedImages, currentView, overlayState, unassignedImages, setUnassignedImages,
  onQueueImage,
  onViewSelect,
  onReset,
  onDeleteView,
  onOverlayToggle,
  onRealtimeOverlayToggle,
  onSetCustomLandmark,
  onClearAllPelvisPins,
  onTranslatePelvisRedLine,
  onSetPoseLandmark,
  onManualPlumbUpdate,
  onCaptureHandlersReady, poseLandmarker, captureMode = 'guided', onCaptureModeChange,
  onShowTips, onReanalyze, centerPanelMessage, classification, showClassificationOverlay, onToggleClassificationOverlay,
  rightReservationPx = 0,
  maxCanvasHeightPx,
  experimentalFeaturesEnabled = false,
  fullscreenEditHistory,
  onEditGestureStart,
  analysisPanelEnabled = true,
  onToggleAnalysisPanel,
  showAnalysisDashboardToggle = false,
  analysisData = {},
  model3D = null,
  immersiveFullscreen = false,
  fillAvailable = false,
}) => {
  type PelvisPinKey = 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS';
  const mode = usePostureMode();
  const { laymanMode } = mode;
  const overlayCaptureSettings = useMemo(
    () =>
      shouldShowPelvisOverlay(mode)
        ? captureSettings
        : {
            ...captureSettings,
            showPelvisLine: false,
            showPelvisLandmarks: false,
          },
    [captureSettings, mode],
  );
  const viewDisplayLabel = useCallback(
    (view: CaptureSequence) => (laymanMode ? getLaymanViewLabel(view) : view),
    [laymanMode],
  );
  const isMobile = useIsMobile();
  const { isChromeHidden, isPostureExtendedChrome, isImmersiveChromeEnabled } =
    usePageHeaderVisibility();
  const mobileEditMode = shouldUseMobilePoseEditMode(mode, isMobile, !!onSetPoseLandmark);
  const inlineCanvasTouchAction = inlineCaptureCanvasTouchAction(mode, isMobile, mobileEditMode);
  const [mobileStickyPoseIndex, setMobileStickyPoseIndex] = useState<number | null>(null);
  const [mobileStickyPinKey, setMobileStickyPinKey] = useState<PelvisPinKey | null>(null);
  const mobileStickyPoseIndexRef = useRef<number | null>(null);
  const mobileStickyPinKeyRef = useRef<PelvisPinKey | null>(null);
  const mobilePoseEmptyDownRef = useRef<{ clientX: number; clientY: number; pointerId: number } | null>(null);
  const mobilePinEmptyDownRef = useRef<{ clientX: number; clientY: number; pointerId: number } | null>(null);

  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fillFrameRef = useRef<HTMLDivElement>(null);
  const fullscreenCanvasRef = useRef<HTMLCanvasElement>(null);
  /** Same dimensions and viewport transform as fullscreen canvas, image only (no grid / pose / pelvis). Used by the magnifier. */
  const fullscreenImageOnlyCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const fullscreenMagnifierCanvasRef = useRef<HTMLCanvasElement>(null);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const requestRef = useRef<number | null>(null);

  /** Auto-detected pose-landmark baseline per view, snapshotted on fresh detection (pre-edit) for snap-back. */
  const autoLandmarksRef = useRef<Partial<Record<CaptureSequence, Landmark[]>>>({});
  /** Previous capture status per view, used to detect a transition INTO `complete` (a fresh detection vs an edit). */
  const prevStatusRef = useRef<Partial<Record<CaptureSequence, string>>>({});

  const [isCameraActive, setIsCameraActive] = useState(false);
  const [videoDevices, setVideoDevices] = useState<MediaDeviceInfo[]>([]);
  const [currentDeviceIndex, setCurrentDeviceIndex] = useState(0);
  const [streamRefState, setStreamRefState] = useState<MediaStream | null>(null);

  // Real-time detection state
  const [realtimeLandmarks, setRealtimeLandmarks] = useState<Landmark[] | null>(null);
  const realtimeLandmarksRef = useRef<Landmark[] | null>(null);
  const lastPushedLandmarksRef = useRef<Landmark[] | null>(null);
  const feedbackStateRef = useRef<CaptureFeedback | null>(null);
  const captureSettingsRef = useRef(captureSettings);
  const [feedback, setFeedback] = useState<CaptureFeedback | null>(null);
  const [isCountdownActive, setIsCountdownActive] = useState(false);

  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
  const [isFullscreenMagnifierEnabled, setIsFullscreenMagnifierEnabled] = useState(true);
  /** Image-space anchor for the magnifier (stable when fullscreen zoom/pan changes). */
  const [fullscreenMagnifierImagePoint, setFullscreenMagnifierImagePoint] = useState<{ x: number; y: number } | null>(
    null
  );
  const [showFullscreenMagnifier, setShowFullscreenMagnifier] = useState(false);
  const [fullscreenZoom, setFullscreenZoom] = useState(1);
  const [fullscreenPan, setFullscreenPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoomPanelOffset, setZoomPanelOffset] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [isDraggingZoomPanel, setIsDraggingZoomPanel] = useState(false);
  const zoomPanelDragRef = useRef<{ startX: number; startY: number; offsetX: number; offsetY: number } | null>(null);
  const [visibleCenterMessage, setVisibleCenterMessage] = useState<string | null>(null);
  const [pelvisPlacementStep, setPelvisPlacementStep] = useState<
    'none' | 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS'
  >('none');
  const [hoveredLandmarkIndex, setHoveredLandmarkIndex] = useState<number | null>(null);
  const [hoveredLineSegment, setHoveredLineSegment] = useState<[number, number] | null>(null);
  const [hoveredPelvisPinKey, setHoveredPelvisPinKey] = useState<PelvisPinKey | null>(null);
  const [hoveredPelvisLine, setHoveredPelvisLine] = useState(false);
  const [activeDragPoseIndex, setActiveDragPoseIndex] = useState<number | null>(null);
  const [activeDragPinKey, setActiveDragPinKey] = useState<PelvisPinKey | null>(null);
  const [isManualPlumbMode, setIsManualPlumbMode] = useState(false);
  const [showConorExample, setShowConorExample] = useState(false);
  const centerMessageTimeout = useRef<number | null>(null);
  const {
    tilt: deviceTilt,
    requestPermission: requestOrientationPermission,
    isSupported: deviceOrientationSupported,
  } = useDeviceOrientation();
  const deviceTiltRef = useRef<{ gamma: number; beta: number } | null>(null);
  const onShowTipsRef = useRef<CaptureModuleProps['onShowTips']>(onShowTips);

  const [capturedAspectRatios, setCapturedAspectRatios] = useState<Partial<Record<CaptureSequence, number>>>({});

  useEffect(() => {
    deviceTiltRef.current = deviceTilt;
  }, [deviceTilt]);

  useEffect(() => {
    onShowTipsRef.current = onShowTips;
  }, [onShowTips]);

  useEffect(() => {
    captureSettingsRef.current = captureSettings;
  }, [captureSettings]);

  // Snapshot the auto-detected pose-landmark baseline on a fresh detection (status transitions INTO 'complete'),
  // and clear it when the view re-enters processing/analyzing so a reanalyze re-snapshots. Edits keep status
  // 'complete', so they do NOT overwrite the baseline.
  useEffect(() => {
    if (!currentView) return;
    const img = capturedImages[currentView];
    const status = img?.status;
    const prev = prevStatusRef.current[currentView];
    if (status === 'processing' || status === 'analyzing') {
      delete autoLandmarksRef.current[currentView];
    } else if (status === 'complete' && prev !== 'complete' && img?.landmarks) {
      autoLandmarksRef.current[currentView] = img.landmarks.map((l) => ({ ...l }));
    }
    prevStatusRef.current[currentView] = status ?? undefined;
  }, [currentView, capturedImages]);

  // Snap a dragged pose landmark back to its auto-detected position when within the configured radius (image space).
  const snapPoseLandmark = (index: number, candidate: { x: number; y: number }) => {
    if (!captureSettings.landmarkSnap || !currentView) return candidate;
    const base = autoLandmarksRef.current[currentView]?.[index];
    if (!base) return candidate;
    const r = clampSnapRadius(captureSettings.landmarkSnapRadius, LANDMARK_SNAP_DEFAULT_PX);
    const d = Math.hypot(candidate.x - base.x, candidate.y - base.y);
    return d <= r ? { x: base.x, y: base.y } : candidate;
  };

  // Snap a dragged ASIS/PSIS pin back to its auto-inferred position when within the configured radius (image space).
  const snapCustomPin = (key: PelvisPinKey, candidate: { x: number; y: number }) => {
    if (!captureSettings.landmarkSnap || !currentView) return candidate;
    const lms = capturedImages[currentView]?.landmarks;
    if (!lms) return candidate;
    const auto = inferPelvisPinsForView(currentView, lms, {
      minVisibility: captureSettings.visibilityThreshold ?? 0.35,
    });
    const a = auto?.[key];
    if (!a) return candidate;
    const r = clampSnapRadius(captureSettings.landmarkSnapRadius, LANDMARK_SNAP_DEFAULT_PX);
    const d = Math.hypot(candidate.x - a.x, candidate.y - a.y);
    return d <= r ? { x: a.x, y: a.y } : candidate;
  };

  const hoverLandmarkIndexForDraw = useMemo(() => {
    if (mobileEditMode && mobileStickyPoseIndex !== null) return mobileStickyPoseIndex;
    return hoveredLandmarkIndex;
  }, [mobileEditMode, mobileStickyPoseIndex, hoveredLandmarkIndex]);

  const hoverLineSegmentForDraw = useMemo(() => {
    if (mobileEditMode && mobileStickyPoseIndex !== null) return null;
    return hoveredLineSegment;
  }, [mobileEditMode, mobileStickyPoseIndex, hoveredLineSegment]);

  const hoverPelvisPinKeyForDraw = useMemo(() => {
    if (mobileEditMode && mobileStickyPinKey !== null) return mobileStickyPinKey;
    return hoveredPelvisPinKey;
  }, [mobileEditMode, mobileStickyPinKey, hoveredPelvisPinKey]);

  const hoverPelvisLineForDraw = useMemo(() => {
    if (mobileEditMode && mobileStickyPinKey !== null) return false;
    return hoveredPelvisLine;
  }, [mobileEditMode, mobileStickyPinKey, hoveredPelvisLine]);

  const getPoseLandmarkLabel = useCallback((index: number): string => {
    const labels: Record<number, string> = {
      7: 'Left Ear',
      8: 'Right Ear',
      11: 'Left Shoulder',
      12: 'Right Shoulder',
      23: 'Left Hip',
      24: 'Right Hip',
      25: 'Left Knee',
      26: 'Right Knee',
      27: 'Left Ankle',
      28: 'Right Ankle',
    };
    return labels[index] ?? `Landmark ${index}`;
  }, []);

  const getPinLabel = useCallback((pinKey: PelvisPinKey): string => {
    const labels: Record<PelvisPinKey, string> = {
      ASIS: 'ASIS',
      PSIS: 'PSIS',
      L_ASIS: 'Left ASIS',
      R_ASIS: 'Right ASIS',
      L_PSIS: 'Left PSIS',
      R_PSIS: 'Right PSIS',
    };
    return labels[pinKey];
  }, []);

  const activeFullscreenLandmarkLabel = useMemo(() => {
    if (!showFullscreenMagnifier) return null;
    if (activeDragPinKey) return getPinLabel(activeDragPinKey);
    if (activeDragPoseIndex !== null) return getPoseLandmarkLabel(activeDragPoseIndex);
    return null;
  }, [showFullscreenMagnifier, activeDragPinKey, activeDragPoseIndex, getPinLabel, getPoseLandmarkLabel]);

  useEffect(() => {
    mobileStickyPoseIndexRef.current = mobileStickyPoseIndex;
  }, [mobileStickyPoseIndex]);

  useEffect(() => {
    mobileStickyPinKeyRef.current = mobileStickyPinKey;
  }, [mobileStickyPinKey]);

  useEffect(() => {
    setMobileStickyPoseIndex(null);
    setMobileStickyPinKey(null);
    mobileStickyPoseIndexRef.current = null;
    mobileStickyPinKeyRef.current = null;
    setIsManualPlumbMode(false);
    if (plumbEditAutoExitTimerRef.current) {
      window.clearTimeout(plumbEditAutoExitTimerRef.current);
      plumbEditAutoExitTimerRef.current = null;
    }
  }, [currentView]);

  useEffect(() => {
    if (!mobileEditMode) {
      setMobileStickyPoseIndex(null);
      setMobileStickyPinKey(null);
      mobileStickyPoseIndexRef.current = null;
      mobileStickyPinKeyRef.current = null;
    }
  }, [mobileEditMode]);

  const streamRef = useRef<MediaStream | null>(null);
  const plumbEditAutoExitTimerRef = useRef<number | null>(null);
  const plumbHandleDragRef = useRef<{ pointerId: number; edge: 'top' | 'bottom' } | null>(null);
  const currentDeviceIdRef = useRef<string | undefined>(undefined);
  const isRestartingRef = useRef(false);
  const cameraViewportRef = useRef<HTMLDivElement | null>(null);
  const autoCapturePendingRef = useRef(false);
  /**
   * S3b: `cameraViewportRef` is the native-fullscreen target, and the UA stylesheet forces a
   * fullscreen element to 100% × 100% (`!important`, so it beats our inline width/height). The media
   * child therefore has to stop being explicitly sized and grow into the space instead. Tracked as
   * React state rather than a `:fullscreen` CSS variant because jsdom cannot match the pseudo-class
   * and this needs a regression test.
   */
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  /**
   * Intrinsic aspect (width / height) of the live stream, read off the video once metadata lands.
   * Camera constraints only ever request landscape, so a desktop webcam letterboxes into a tall 3:4
   * box; the media area uses this to shed the dead bands. Portrait phone streams clamp to the
   * existing height, which makes this a no-op on the device that actually does the capturing.
   */
  const [liveStreamAspect, setLiveStreamAspect] = useState<number | null>(null);
  /** Set in drawCanvas for converting pointer coords to image space (for pose landmark hit-test). */
  const canvasToImageTransformMapRef = useRef(new WeakMap<HTMLCanvasElement, {
    centerX: number;
    centerY: number;
    imgCenterX: number;
    imgCenterY: number;
    zoomScale: number;
    rotationDeg: number;
  }>());

  useEffect(() => {
    if (!centerPanelMessage) return;
    setVisibleCenterMessage(centerPanelMessage);
    if (centerMessageTimeout.current) {
      window.clearTimeout(centerMessageTimeout.current);
    }
    centerMessageTimeout.current = window.setTimeout(() => {
      setVisibleCenterMessage(null);
      centerMessageTimeout.current = null;
    }, 2500);
    return () => {
      if (centerMessageTimeout.current) {
        window.clearTimeout(centerMessageTimeout.current);
        centerMessageTimeout.current = null;
      }
    };
  }, [centerPanelMessage]);

  // Helper function to determine if current camera is front-facing
  const isFrontCamera = useCallback(async (deviceId?: string): Promise<boolean> => {
    if (deviceId) {
      // If we have a specific device, check its label or capabilities
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const currentDevice = devices.find(d => d.deviceId === deviceId);
        if (currentDevice && currentDevice.label) {
          // Check label for front camera indicators
          const label = currentDevice.label.toLowerCase();
          return label.includes('front') || label.includes('user') || label.includes('facing');
        }
      } catch (e) {
        const domErr = e as DOMException;
        logger.warn('PostureCapture: could not enumerate devices for mirror detection', {
          category: LogCategory.CAMERA,
          camera: true,
          errorName: domErr?.name,
          errorMessage: (e as Error)?.message,
        });
      }
    }
    // If no specific device or label check failed, use default facing mode setting
    return captureSettings.defaultFacingMode === 'user';
  }, [captureSettings.defaultFacingMode]);

  // Apply mirror transform based on current settings and camera
  const applyMirrorTransform = useCallback(async (deviceId?: string) => {
    if (!videoRef.current || !isCameraActive) return;

    const isFront = await isFrontCamera(deviceId);
    if (captureSettings.mirrorFrontCamera && isFront) {
      videoRef.current.style.transform = 'scaleX(-1)';
    } else {
      videoRef.current.style.transform = 'none';
    }
  }, [captureSettings.mirrorFrontCamera, isCameraActive, isFrontCamera]);

  // Watch for mirror setting changes and apply immediately (without restarting camera)
  useEffect(() => {
    if (isCameraActive && !isRestartingRef.current) {
      applyMirrorTransform(currentDeviceIdRef.current);
    }
  }, [captureSettings.mirrorFrontCamera, isCameraActive, applyMirrorTransform]);

  // Watch for camera configuration changes (resolution, facing mode) and restart camera
  const prevResolutionRef = useRef(captureSettings.preferredResolution);
  const prevFacingModeRef = useRef(captureSettings.defaultFacingMode);
  const startCameraRef = useRef<((deviceId?: string) => Promise<void>) | null>(null);

  // Determine next view for guided flow (Posterior excluded when experimental off or mode restricts views)
  const effectiveSequence = useMemo((): CaptureSequence[] => {
    if (mode.allowedViews.length < VIEW_SEQUENCE.length) {
      return [...mode.allowedViews];
    }
    return getEffectiveViewSequence(experimentalFeaturesEnabled);
  }, [mode.allowedViews, experimentalFeaturesEnabled]);
  /** Next slot that still needs a picture (or a retry). Null when every required view is complete. */
  const nextPendingCaptureView: CaptureSequence | null =
    effectiveSequence.find((v) => !capturedImages[v] || capturedImages[v]?.status === 'failed') ?? null;

  // After shutter, parent sets status to `queued` until analysis finishes — not `complete` yet.
  // Use last view that already has imageData so the wizard stays on step 3 (not step 1) during that gap.
  const lastViewWithCapturedFrameInFlow = [...effectiveSequence]
    .reverse()
    .find((v) => !!capturedImages[v]?.imageData);

  // Guided UI / overlays: pending slot first; else latest frame in the sequence (queued or complete).
  const nextView: CaptureSequence =
    nextPendingCaptureView ??
    lastViewWithCapturedFrameInFlow ??
    effectiveSequence[effectiveSequence.length - 1]!;

  const currentStepIndex = nextPendingCaptureView
    ? effectiveSequence.indexOf(nextPendingCaptureView)
    : lastViewWithCapturedFrameInFlow
      ? effectiveSequence.indexOf(lastViewWithCapturedFrameInFlow)
      : Math.max(0, effectiveSequence.length - 1);

  const isCaptureComplete = effectiveSequence.every((v) => capturedImages[v]?.status === 'complete');
  const currentImage = currentView ? capturedImages[currentView] : null;

  useEffect(() => {
    if (!currentImage?.imageData || !currentView) return;
    const img = new Image();
    img.onload = () => {
      if (img.width > 0 && img.height > 0) {
        const ratio = img.width / img.height;
        setCapturedAspectRatios((prev) => (prev[currentView] === ratio ? prev : { ...prev, [currentView]: ratio }));
      }
    };
    img.src = currentImage.imageData;
  }, [currentImage?.imageData, currentView]);
  const currentAnchorOffsetPx = 0;
  const liveFloorCorrectionDeg = useMemo(() => {
    if (currentImage?.status === 'complete' && Number.isFinite(currentImage.floorCorrectionDeg ?? NaN)) {
      return currentImage.floorCorrectionDeg ?? 0;
    }
    return 0;
  }, [currentImage?.floorCorrectionDeg, currentImage?.status]);
  const activePlumbHandleEdge: 'top' | 'bottom' = captureSettings.toolbarPosition === 'top' ? 'bottom' : 'top';

  const clearPlumbAutoExitTimer = useCallback(() => {
    if (plumbEditAutoExitTimerRef.current) {
      window.clearTimeout(plumbEditAutoExitTimerRef.current);
      plumbEditAutoExitTimerRef.current = null;
    }
  }, []);

  const schedulePlumbAutoExit = useCallback(() => {
    clearPlumbAutoExitTimer();
    plumbEditAutoExitTimerRef.current = window.setTimeout(() => {
      setIsManualPlumbMode(false);
      setVisibleCenterMessage('PL edit mode ended');
      plumbEditAutoExitTimerRef.current = null;
    }, 3000);
  }, [clearPlumbAutoExitTimer]);

  const captureSlotKey = nextPendingCaptureView ?? 'complete';
  const stabilityResetKey = useMemo(
    () => `${captureSlotKey}|${isSettingsOpen}|${captureSettings.toolbarPosition}`,
    [captureSlotKey, isSettingsOpen, captureSettings.toolbarPosition]
  );

  const bodyReady = useMemo(
    () => hasMinimumLateralChainForCapture(realtimeLandmarks, captureSettings.visibilityThreshold),
    [realtimeLandmarks, captureSettings.visibilityThreshold]
  );

  // Stability Detection - uses capture settings
  const { isStable, variance, stableDuration } = useStabilityDetection(realtimeLandmarks, {
    varianceThreshold: captureSettings.stabilityThreshold,
    stableDurationMs: captureSettings.holdDuration * 1000,
    visibilityThreshold: captureSettings.visibilityThreshold,
    resetKey: stabilityResetKey,
  });
  const autoCaptureEligibleRef = useRef(false);

  // Auto-Capture Trigger — requires full lateral chain, stable pose, no settings sheet (#7239)
  useEffect(() => {
    const autoCaptureEligible =
      captureSettings.enableAutoCapture &&
      !isSettingsOpen &&
      isCameraActive &&
      captureMode === 'guided' &&
      !isCaptureComplete &&
      bodyReady &&
      isStable;

    if (autoCaptureEligible !== autoCaptureEligibleRef.current) {
      autoCaptureEligibleRef.current = autoCaptureEligible;
      logger.debug('PostureCapture: auto-capture eligibility changed', {
        category: LogCategory.UI,
        countdown: true,
        eligible: autoCaptureEligible,
        enableAutoCapture: captureSettings.enableAutoCapture,
        isSettingsOpen,
        isStable,
        isCameraActive,
        captureMode,
        isCaptureComplete,
        bodyReady,
        countdownActive: isCountdownActive,
        stableDurationMs: Math.round(stableDuration),
        variance,
      });
    }

    if (!captureSettings.enableAutoCapture) {
      if (isCountdownActive) setIsCountdownActive(false);
      autoCapturePendingRef.current = false;
      return;
    }
    if (isSettingsOpen) {
      if (isCountdownActive) setIsCountdownActive(false);
      autoCapturePendingRef.current = false;
      return;
    }
    if (!isCameraActive || captureMode !== 'guided' || isCaptureComplete) {
      if (isCountdownActive) setIsCountdownActive(false);
      autoCapturePendingRef.current = false;
      return;
    }
    if (!bodyReady || !isStable) {
      if (isCountdownActive) setIsCountdownActive(false);
      autoCapturePendingRef.current = false;
      return;
    }
    if (!isCountdownActive) {
      logger.info('PostureCapture: starting countdown', {
        category: LogCategory.UI,
        countdown: true,
        nextPendingCaptureView,
        stableDurationMs: Math.round(stableDuration),
        variance,
        configuredCountdownSeconds: captureSettings.countdownDuration,
      });
      setIsCountdownActive(true);
      autoCapturePendingRef.current = true;
    }
  }, [
    isStable,
    isCountdownActive,
    isCameraActive,
    captureMode,
    isCaptureComplete,
    captureSettings.enableAutoCapture,
    captureSettings.countdownDuration,
    nextPendingCaptureView,
    isSettingsOpen,
    bodyReady,
    stableDuration,
    variance,
  ]);

  useEffect(() => {
    logger.debug('PostureCapture: countdown active changed', {
      category: LogCategory.UI,
      countdown: true,
      isCountdownActive,
      nextPendingCaptureView,
      guidedDisplayView: nextView,
    });
  }, [isCountdownActive, nextPendingCaptureView, nextView]);

  const startCamera = useCallback(async (deviceId?: string) => {
    // Clear any previous failure so a retry does not show a stale error notice.
    setFeedback(null);

    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
    }

    // Store current device ID for mirror detection
    currentDeviceIdRef.current = deviceId;

    // Use capture settings for camera constraints
    const constraints = getCameraConstraints(captureSettings, deviceId);

    logger.debug('PostureCapture: requesting getUserMedia', {
      category: LogCategory.CAMERA,
      camera: true,
      deviceId: deviceId ? 'specific' : 'default',
      constraints: JSON.stringify(constraints),
    });

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.play().catch((e: unknown) => {
          const domErr = e as DOMException;
          logger.error('PostureCapture: camera autoplay failed', {
            category: LogCategory.CAMERA,
            camera: true,
            errorName: domErr?.name,
            errorMessage: (e as Error)?.message,
          });
        });

        // Wait for video to be ready, then apply mirror transform
        const applyMirror = async () => {
          await applyMirrorTransform(deviceId);
        };

        videoRef.current.addEventListener('loadedmetadata', applyMirror, { once: true });

        // Also apply immediately in case metadata is already loaded
        if (videoRef.current.readyState >= 1) {
          await applyMirror();
        }
      }
      streamRef.current = stream;
      setStreamRefState(stream);
      setIsCameraActive(true);

      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoInputs = devices.filter(d => d.kind === 'videoinput');
      setVideoDevices(videoInputs);

      logger.info('PostureCapture: camera started successfully', {
        category: LogCategory.CAMERA,
        camera: true,
        trackCount: stream.getTracks().length,
        deviceCount: videoInputs.length,
        labels: videoInputs.map(d => d.label || '(no label)'),
      });
    } catch (e: unknown) {
      const domErr = e as DOMException;
      logger.error('PostureCapture: camera permission error', {
        category: LogCategory.CAMERA,
        camera: true,
        errorName: domErr?.name,
        errorCode: domErr?.code,
        errorMessage: (e as Error)?.message,
        stack: (e as Error)?.stack?.slice(0, 500),
      });
      // More specific error messages
      let errorMessage = "Camera access denied. Please check permissions.";
      if (domErr?.name === 'NotFoundError') {
        errorMessage = "No camera found on this device.";
      } else if (domErr?.name === 'NotAllowedError') {
        errorMessage = "Camera permission denied. Please allow camera access.";
      } else if (domErr?.name === 'OverconstrainedError') {
        errorMessage = "Camera doesn't support requested resolution. Try a lower setting.";
      } else if (domErr?.name === 'NotReadableError') {
        errorMessage = "Camera is in use by another application.";
      }
      setFeedback({ message: errorMessage, type: 'error' });
    }
  }, [captureSettings, applyMirrorTransform]);

  // Store startCamera in ref for use in effects
  startCameraRef.current = startCamera;

  // Watch for camera configuration changes (resolution, facing mode) and restart camera
  useEffect(() => {
    const resolutionChanged = prevResolutionRef.current !== captureSettings.preferredResolution;
    const facingModeChanged = prevFacingModeRef.current !== captureSettings.defaultFacingMode;

    if (isCameraActive && (resolutionChanged || facingModeChanged) && startCameraRef.current) {
      isRestartingRef.current = true;
      const currentDeviceId = currentDeviceIdRef.current;
      // Use setTimeout to avoid calling startCamera during render
      setTimeout(() => {
        startCameraRef.current?.(currentDeviceId).finally(() => {
          isRestartingRef.current = false;
        });
      }, 0);
    }

    prevResolutionRef.current = captureSettings.preferredResolution;
    prevFacingModeRef.current = captureSettings.defaultFacingMode;
  }, [captureSettings.preferredResolution, captureSettings.defaultFacingMode, isCameraActive]);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
      setStreamRefState(null);
    }
    setIsCameraActive(false);
    realtimeLandmarksRef.current = null;
    lastPushedLandmarksRef.current = null;
    feedbackStateRef.current = null;
    setRealtimeLandmarks(null);
    setFeedback(null);
    if (requestRef.current) cancelAnimationFrame(requestRef.current);
  }, []);

  const completeViewsForFullscreen = useMemo(
    () =>
      effectiveSequence.filter((v) => capturedImages[v]?.status === 'complete'),
    [effectiveSequence, capturedImages]
  );

  const cycleFullscreenView = useCallback(
    (delta: -1 | 1) => {
      const rotatable = effectiveSequence.filter((v) => capturedImages[v]?.status === 'complete');
      if (rotatable.length === 0) return;
      let idx = currentView ? rotatable.indexOf(currentView) : 0;
      if (idx < 0) idx = 0;
      const next = rotatable[(idx + delta + rotatable.length) % rotatable.length];
      stopCamera();
      onViewSelect(next);
    },
    [effectiveSequence, capturedImages, currentView, onViewSelect, stopCamera]
  );

  const handleCloseCameraView = useCallback(() => {
    logger.info('PostureCapture: user closed camera view', {
      category: LogCategory.UI,
      camera: true,
    });
    setIsCountdownActive(false);
    autoCapturePendingRef.current = false;
    stopCamera();
  }, [stopCamera]);

  useEffect(() => {
    return () => { // Cleanup on unmount
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      clearPlumbAutoExitTimer();
    };
  }, [clearPlumbAutoExitTimer]);

  // Enter browser fullscreen for an immersive picture-only camera viewport — phones only.
  // On desktop the camera stays inside the normal window layout (practitioners keep
  // browser chrome and the rest of the app visible while capturing).
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const viewport = cameraViewportRef.current;
    if (!viewport) return;

    if (isCameraActive && isMobile) {
      if (!document.fullscreenElement && viewport.requestFullscreen) {
        viewport.requestFullscreen().catch(() => {
          // Ignore failures (e.g. browser policy); capture still works inline.
        });
      }
      return;
    }

    if (document.fullscreenElement === viewport && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {
        // Ignore failures so stop-camera flow remains resilient.
      });
    }
  }, [isCameraActive, isMobile]);

  // Fullscreen can also be left by gesture or Esc, so the flag follows the event rather than our
  // own request — otherwise the media area stays in flex-fill mode inside a normally-sized card.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const sync = () => setIsNativeFullscreen(document.fullscreenElement === cameraViewportRef.current);
    sync();
    document.addEventListener('fullscreenchange', sync);
    return () => document.removeEventListener('fullscreenchange', sync);
  }, [isCameraActive]);

  // A stopped camera must not leave a stale aspect behind for the next stream (or for the
  // captured-image state, which is always 3:4).
  useEffect(() => {
    if (!isCameraActive) setLiveStreamAspect(null);
  }, [isCameraActive]);

  // Real-time Detection Loop
  const detectFrame = useCallback(async () => {
    if (!videoRef.current || !poseLandmarker || !isCameraActive) return;

    const video = videoRef.current;
    if (video.videoWidth === 0 || video.videoHeight === 0) {
      requestRef.current = requestAnimationFrame(detectFrame);
      return;
    }

    let startTimeMs = performance.now();

    // Detect landmarks
    try {
      const result = poseLandmarker.detectForVideo(video, startTimeMs);
      const landmarksRaw = result.landmarks[0];

      if (landmarksRaw) {
        // Convert to normalized format
        const normalized: Landmark[] = landmarksRaw.map((p: any) => ({
          x: p.x * video.videoWidth,
          y: p.y * video.videoHeight,
          z: p.z,
          visibility: p.visibility
        }));
        realtimeLandmarksRef.current = normalized;

        if (landmarksMeaningfullyChanged(lastPushedLandmarksRef.current, normalized)) {
          lastPushedLandmarksRef.current = normalized;
          setRealtimeLandmarks(normalized);
        }

        const nextFeedback = deriveRealtimeCaptureFeedback(
          normalized,
          captureSettingsRef.current.visibilityThreshold,
        );
        if (!captureFeedbackEquals(feedbackStateRef.current, nextFeedback)) {
          feedbackStateRef.current = nextFeedback;
          setFeedback(nextFeedback);
        }
      } else {
        realtimeLandmarksRef.current = null;
        if (landmarksMeaningfullyChanged(lastPushedLandmarksRef.current, null)) {
          lastPushedLandmarksRef.current = null;
          setRealtimeLandmarks(null);
        }
        const nextFeedback = deriveRealtimeCaptureFeedback(null, captureSettingsRef.current.visibilityThreshold);
        if (!captureFeedbackEquals(feedbackStateRef.current, nextFeedback)) {
          feedbackStateRef.current = nextFeedback;
          setFeedback(nextFeedback);
        }
      }
    } catch (err) {
      logger.warn("Detection error", err);
    }

    requestRef.current = requestAnimationFrame(detectFrame);
  }, [poseLandmarker, isCameraActive]);

  useEffect(() => {
    if (isCameraActive && poseLandmarker) {
      requestRef.current = requestAnimationFrame(detectFrame);
    }
    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [isCameraActive, poseLandmarker, detectFrame]);

  const handleCaptureClick = useCallback(() => {
    if (!isCameraActive) {
      startCamera();
      onShowTipsRef.current?.();
      void requestOrientationPermission();
      return;
    }
    if (
      captureMode === 'guided' &&
      autoCapturePendingRef.current &&
      !hasMinimumLateralChainForCapture(realtimeLandmarksRef.current, captureSettings.visibilityThreshold)
    ) {
      autoCapturePendingRef.current = false;
      setIsCountdownActive(false);
      logger.info('PostureCapture: auto-capture cancelled — minimum landmark chain not met at shutter', {
        category: LogCategory.CAMERA,
        camera: true,
      });
      return;
    }
    const targetView = nextPendingCaptureView;
    if (!targetView || !videoRef.current) {
      autoCapturePendingRef.current = false;
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      autoCapturePendingRef.current = false;
      return;
    }

    autoCapturePendingRef.current = false;

    ctx.drawImage(videoRef.current, 0, 0);
    playPostureShutterSound();

    // Apply image enhancements and transformations
    const enhancedCanvas = applyImageEnhancements(canvas, captureSettings);

    // Compress before sending to parent to reduce memory usage & fit in localStorage
    const dataUrl = enhancedCanvas.toDataURL('image/jpeg', 0.6);
    compressImage(dataUrl).then(compressedUrl => {
      onQueueImage(compressedUrl, targetView);
    }).catch(err => {
      logger.error('Capture compression failed, using enhanced canvas', err);
      onQueueImage(dataUrl, targetView);
    });

    setIsCountdownActive(false); // Reset countdown
  }, [isCameraActive, startCamera, nextPendingCaptureView, onQueueImage, captureSettings, captureMode, realtimeLandmarks]);

  const handleCountdownComplete = useCallback(() => {
    logger.info('PostureCapture: countdown complete -> capturing image', {
      category: LogCategory.UI,
      countdown: true,
      nextPendingCaptureView,
    });
    handleCaptureClick();
  }, [handleCaptureClick, nextPendingCaptureView]);

  const handleCountdownCancel = useCallback(() => {
    logger.info('PostureCapture: countdown cancelled by user', {
      category: LogCategory.UI,
      countdown: true,
      nextPendingCaptureView,
    });
    setIsCountdownActive(false);
    autoCapturePendingRef.current = false;
  }, [nextPendingCaptureView]);

  const switchCamera = useCallback(() => {
    if (videoDevices.length <= 1) {
      logger.debug('PostureCapture: only one camera available, cannot switch', {
        category: LogCategory.CAMERA,
        camera: true,
        deviceCount: videoDevices.length,
      });
      return;
    }
    const nextIndex = (currentDeviceIndex + 1) % videoDevices.length;
    setCurrentDeviceIndex(nextIndex);
    const nextDeviceId = videoDevices[nextIndex].deviceId;
    startCamera(nextDeviceId);
  }, [videoDevices, currentDeviceIndex, startCamera]);

  const handleResetOverlay = useCallback(() => {
    onCaptureSettingsChange({
      ...captureSettings,
      rotation: 0,
      zoom: 0,
      perspectiveX: 0,
      perspectiveY: 0,
      tilt: 0,
      yaw: 0,
    });
    // Re-run pose detection for current view to restore original landmark positions
    if (currentView && capturedImages[currentView]?.imageData && capturedImages[currentView]?.status === 'complete') {
      onQueueImage(capturedImages[currentView].imageData, currentView, {
        resetToInitialDetection: true,
        preservePlumbline: true,
      });
    }
  }, [captureSettings, onCaptureSettingsChange, currentView, capturedImages, onQueueImage]);

  const handleUploadChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    logger.debug('PostureCapture: starting picture upload', {
      category: LogCategory.UI,
      fileCount: files?.length || 0,
    });
    if (!files || files.length === 0) return;

    const detectViewFromFilename = (name: string): CaptureSequence | null => {
      const n = (name || '').toLowerCase().replace(/\.(jpg|jpeg|png|heic|webp|gif)$/, ''); const tokens = n.split(/[\s_\-]+/).filter(Boolean);
      const has = (...keys: string[]) => tokens.some(t => keys.includes(t));
      if (has('anterior', 'front', 'frontal', 'ap')) return 'Anterior';
      if (has('posterior', 'back', 'dorsal', 'pa')) return 'Posterior';
      if (has('left', 'lt')) return 'Left';
      if (has('right', 'rt')) return 'Right';
      return null;
    };

    Array.from(files).forEach(file => {
      const detectedView = detectViewFromFilename(file.name);
      if (isSelfAssessment) {
        trackPhotoUploadStart(detectedView || currentView || 'unassigned', {
          fileType: file.type,
          fileSize: file.size,
        });
      }
      // When experimental is off, do not auto-assign Posterior; put in unassigned
      const canAssignToView = detectedView && !capturedImages[detectedView] &&
        (detectedView !== 'Posterior' || experimentalFeaturesEnabled);

      // Compress immediately on upload
      compressImage(file).then(compressedUrl => {
        if (canAssignToView) {
          onQueueImage(compressedUrl, detectedView!);
        } else {
          setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl: compressedUrl }]);
        }
      }).catch(err => {
        logger.error('Upload compression failed', err);
        // Fallback to raw data URL if compression fails
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string;
          if (dataUrl) {
            if (canAssignToView) {
              onQueueImage(dataUrl, detectedView!);
            } else {
              setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl }]);
            }
          }
        };
        reader.readAsDataURL(file);
      });
    });
    e.target.value = ''; // Reset input
  };

  // Clipboard paste handler: add pasted images to unassigned (no filename to detect view)
  const handlePaste = useCallback((e: ClipboardEvent) => {
    if (!e.clipboardData) return;
    const target = e.target as HTMLElement;
    if (target?.closest?.('input, textarea, [contenteditable="true"]')) return; // Don't intercept paste in text fields
    const imageItems = Array.from(e.clipboardData.items).filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (!file) return;
      compressImage(file).then(compressedUrl => {
        setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl: compressedUrl }]);
      }).catch(err => {
        logger.error('Paste compression failed', err);
        const reader = new FileReader();
        reader.onload = (event) => {
          const dataUrl = event.target?.result as string;
          if (dataUrl) {
            setUnassignedImages(prev => [...prev, { id: Date.now() + Math.random(), dataUrl }]);
          }
        };
        reader.readAsDataURL(file);
      });
    });
  }, [setUnassignedImages]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const drawCanvas = useCallback((targetCanvas?: HTMLCanvasElement | null) => {
    const canvas = targetCanvas ?? canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas if no current view (e.g., after reset)
    if (!currentView) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (canvas === fullscreenCanvasRef.current && fullscreenImageOnlyCanvasRef.current) {
        const b = fullscreenImageOnlyCanvasRef.current;
        const bc = b.getContext('2d');
        if (bc) bc.clearRect(0, 0, b.width, b.height);
      }
      return;
    }

    const imgData = capturedImages[currentView];
    if (!imgData?.imageData) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (canvas === fullscreenCanvasRef.current && fullscreenImageOnlyCanvasRef.current) {
        const b = fullscreenImageOnlyCanvasRef.current;
        const bc = b.getContext('2d');
        if (bc) bc.clearRect(0, 0, b.width, b.height);
      }
      return;
    }

    const img = new Image();
    img.onload = () => {
      // Create a temporary canvas to apply enhancements and transformations
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = img.width;
      tempCanvas.height = img.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (!tempCtx) return;

      tempCtx.drawImage(img, 0, 0);

      // Stored image is already enhanced from capture; skip geometric transforms to avoid
      // double-transforming and landmark misalignment (see Postural_imaging_discrepancies)
      const skipGeometricTransforms = true;
      const enhancedCanvas = applyImageEnhancements(tempCanvas, captureSettings, {
        skipGeometricTransforms,
      });

      // When skipping geometric transforms, displayed image = stored image (1:1); use identity transform
      const zoomScale = skipGeometricTransforms ? 1 : (captureSettings.zoom > 0 ? 1 + (captureSettings.zoom / 100) : 1);
      const rotationDeg = skipGeometricTransforms ? 0 : captureSettings.rotation;
      const landmarks = imgData.landmarks || [];

      const paintFrame = (
        dest: HTMLCanvasElement,
        recordTransform: boolean,
        drawOverlays: boolean
      ) => {
        const c = dest.getContext('2d');
        if (!c) return;
        const isFullscreenDest = dest === fullscreenCanvasRef.current;
        const viewportZoom = isFullscreenDest ? fullscreenZoom : 1;
        const viewportPan = isFullscreenDest ? fullscreenPan : { x: 0, y: 0 };
        const effectiveZoom = zoomScale * viewportZoom;

        dest.width = enhancedCanvas.width;
        dest.height = enhancedCanvas.height;
        c.clearRect(0, 0, dest.width, dest.height);

        const centerX = dest.width / 2 + viewportPan.x;
        const centerY = dest.height / 2 + viewportPan.y;
        const imgCenterX = enhancedCanvas.width / 2;
        const imgCenterY = enhancedCanvas.height / 2;
        if (recordTransform) {
          canvasToImageTransformMapRef.current.set(dest, {
            centerX,
            centerY,
            imgCenterX,
            imgCenterY,
            zoomScale: effectiveZoom,
            rotationDeg,
          });
        }

        c.save();
        c.translate(centerX, centerY);
        if (rotationDeg !== 0) {
          c.rotate((rotationDeg * Math.PI) / 180);
        }
        if (effectiveZoom !== 1) {
          c.scale(effectiveZoom, effectiveZoom);
        }
        c.translate(-imgCenterX, -imgCenterY);
        c.drawImage(enhancedCanvas, 0, 0);

        if (!drawOverlays) {
          c.restore();
          return;
        }

        drawIdealGrid(
          c,
          currentView,
          landmarks,
          overlayCaptureSettings,
          imgData.floorCorrectionDeg ?? 0,
          currentAnchorOffsetPx,
          true,
          activePlumbHandleEdge,
          isManualPlumbMode
        );

        if (imgData.status === 'failed') {
          c.restore();
          c.strokeStyle = 'rgba(239,68,68,0.7)';
          c.lineWidth = 20;
          c.strokeRect(0, 0, dest.width, dest.height);
          return;
        }

        if (overlayState[currentView]?.client && landmarks) {
          const hover: LandmarkHover | null =
            hoverLandmarkIndexForDraw !== null || hoverLineSegmentForDraw !== null
              ? { landmarkIndex: hoverLandmarkIndexForDraw, lineSegment: hoverLineSegmentForDraw }
              : null;
          if (onSetPoseLandmark) {
            drawLandmarks(c, landmarks, overlayCaptureSettings, hover, currentView);
          } else if (currentView === 'Anterior' || currentView === 'Posterior') {
            drawAP(c, landmarks, overlayCaptureSettings, hover);
          } else {
            drawLat(c, landmarks, currentView, imgData.custom || {}, overlayCaptureSettings, hover);
          }
          drawCustomPelvis(
            c,
            currentView,
            imgData.custom || {},
            overlayCaptureSettings,
            activeDragPinKey,
            hoverPelvisPinKeyForDraw,
            hoverPelvisLineForDraw
          );
        }

        drawViewportOrientationLabels(c, currentView, undefined, mode.laymanMode);

        c.restore();
      };

      paintFrame(canvas, true, true);
      if (canvas === fullscreenCanvasRef.current) {
        let base = fullscreenImageOnlyCanvasRef.current;
        if (!base) {
          base = document.createElement('canvas');
          fullscreenImageOnlyCanvasRef.current = base;
        }
        paintFrame(base, false, false);
      }
    };
    img.src = imgData.imageData;
  }, [
    currentView,
    capturedImages,
    overlayState,
    overlayCaptureSettings,
    hoverLandmarkIndexForDraw,
    hoverLineSegmentForDraw,
    onSetPoseLandmark,
    activeDragPinKey,
    hoverPelvisPinKeyForDraw,
    hoverPelvisLineForDraw,
    fullscreenZoom,
    fullscreenPan,
    activePlumbHandleEdge,
    isManualPlumbMode,
    currentAnchorOffsetPx,
  ]);

  useEffect(() => {
    if (!isCameraActive) {
      drawCanvas(canvasRef.current);
      if (isFullScreenOpen) {
        drawCanvas(fullscreenCanvasRef.current);
      }
    }
  }, [isCameraActive, currentView, drawCanvas, isFullScreenOpen]);

  useEffect(() => {
    if (!isFullScreenOpen || isCameraActive) return;
    // Dialog content is portaled/mounted asynchronously; wait one paint cycle
    // before drawing into the fullscreen canvas to avoid an initial black frame.
    let raf2: number | null = null;
    const raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        drawCanvas(fullscreenCanvasRef.current);
      });
    });
    return () => {
      window.cancelAnimationFrame(raf1);
      if (typeof raf2 === 'number') {
        window.cancelAnimationFrame(raf2);
      }
    };
  }, [isFullScreenOpen, isCameraActive, drawCanvas]);

  useEffect(() => {
    if (!isFullScreenOpen || !isFullscreenMagnifierEnabled || !showFullscreenMagnifier || !fullscreenMagnifierImagePoint) {
      return;
    }
    const fs = fullscreenCanvasRef.current;
    const base = fullscreenImageOnlyCanvasRef.current;
    const usingBaseSource = Boolean(base && fs && base.width === fs.width && base.height === fs.height);
    const source = usingBaseSource ? base : fs;
    const target = fullscreenMagnifierCanvasRef.current;
    if (!source || !target) return;
    const tctx = target.getContext('2d');
    if (!tctx) return;

    // If we sample from image-only base, that canvas is unzoomed and unpanned (image space = canvas space).
    // Otherwise (fallback to fullscreen canvas), map image -> canvas with current viewport zoom/pan.
    let canvasPt: { x: number; y: number };
    if (usingBaseSource) {
      canvasPt = {
        x: fullscreenMagnifierImagePoint.x,
        y: fullscreenMagnifierImagePoint.y,
      };
    } else {
      const w = fs.width;
      const h = fs.height;
      if (w <= 0 || h <= 0) return;
      const icx = w / 2;
      const icy = h / 2;
      const centerX = w / 2 + fullscreenPan.x;
      const centerY = h / 2 + fullscreenPan.y;
      const z = fullscreenZoom;
      canvasPt = {
        x: centerX + z * (fullscreenMagnifierImagePoint.x - icx),
        y: centerY + z * (fullscreenMagnifierImagePoint.y - icy),
      };
    }

    const srcSampleSize = 80;
    const targetSize = 160;
    const halfSrc = srcSampleSize / 2;
    const sxRaw = canvasPt.x - halfSrc;
    const syRaw = canvasPt.y - halfSrc;
    const sx = Math.max(0, Math.min(source.width - srcSampleSize, sxRaw));
    const sy = Math.max(0, Math.min(source.height - srcSampleSize, syRaw));

    tctx.clearRect(0, 0, target.width, target.height);
    tctx.imageSmoothingEnabled = false;
    tctx.drawImage(source, sx, sy, srcSampleSize, srcSampleSize, 0, 0, targetSize, targetSize);

    // Thin crosshair only (magnifier samples image-only layer — no pose/pelvis dots).
    const c = targetSize / 2;
    tctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    tctx.lineWidth = 1;
    tctx.beginPath();
    tctx.moveTo(c, 0);
    tctx.lineTo(c, targetSize);
    tctx.moveTo(0, c);
    tctx.lineTo(targetSize, c);
    tctx.stroke();

    // While dragging, punch out the center so the focal pixel stays unobstructed.
    if (activeDragPinKey !== null || activeDragPoseIndex !== null) {
      const holeR = 6;
      tctx.save();
      tctx.globalCompositeOperation = 'destination-out';
      tctx.beginPath();
      tctx.arc(c, c, holeR, 0, Math.PI * 2);
      tctx.fill();
      tctx.restore();

      tctx.strokeStyle = 'rgba(255, 255, 255, 0.75)';
      tctx.lineWidth = 1;
      tctx.beginPath();
      tctx.arc(c, c, holeR, 0, Math.PI * 2);
      tctx.stroke();
    }

    tctx.strokeStyle = 'rgba(255, 255, 255, 0.35)';
    tctx.lineWidth = 1;
    tctx.strokeRect(0.5, 0.5, targetSize - 1, targetSize - 1);
  }, [
    isFullScreenOpen,
    isFullscreenMagnifierEnabled,
    showFullscreenMagnifier,
    fullscreenMagnifierImagePoint,
    fullscreenZoom,
    fullscreenPan,
    drawCanvas,
    activeDragPinKey,
    activeDragPoseIndex,
  ]);

  const handleCanvasClick = (e: React.MouseEvent<HTMLCanvasElement>) => {
    if (!currentView) return;

    const canvas = e.currentTarget as HTMLCanvasElement;
    if (!canvas) return;

    const bm = clientToCanvasBitmapPoint(e.clientX, e.clientY, canvas);
    if (!bm) return;
    const imgPt = canvasToImagePoint(bm, canvas);
    if (!imgPt) return;
    const x = imgPt.x;
    const y = imgPt.y;

    // Placement mode first (works on all views), then legacy keyboard shortcuts for lateral
    let type: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS' | null = null;

    if (pelvisPlacementStep !== 'none') {
      type = pelvisPlacementStep;

      // Auto-advance within the chosen view
      if (currentView === 'Anterior') {
        if (pelvisPlacementStep === 'L_ASIS') {
          setPelvisPlacementStep('R_ASIS');
          setVisibleCenterMessage('Tap Right ASIS (Front of Pelvis)');
        } else {
          setPelvisPlacementStep('none');
          setVisibleCenterMessage(null);
        }
      } else if (currentView === 'Posterior') {
        if (pelvisPlacementStep === 'L_PSIS') {
          setPelvisPlacementStep('R_PSIS');
          setVisibleCenterMessage('Tap Right PSIS (Back of Pelvis)');
        } else {
          setPelvisPlacementStep('none');
          setVisibleCenterMessage(null);
        }
      } else if (currentView === 'Left' || currentView === 'Right') {
        if (pelvisPlacementStep === 'ASIS') {
          setPelvisPlacementStep('PSIS');
          setVisibleCenterMessage('Tap PSIS (Back of Pelvis)');
        } else {
          setPelvisPlacementStep('none');
          setVisibleCenterMessage(null);
        }
      } else {
        setPelvisPlacementStep('none');
        setVisibleCenterMessage(null);
      }
    } else if (currentView === 'Left' || currentView === 'Right') {
      type = e.shiftKey ? 'ASIS' : (e.altKey || e.metaKey ? 'PSIS' : null);
    }

    if (type) {
      onEditGestureStart?.();
      onSetCustomLandmark(currentView, snapCustomPin(type, { x, y }), type);
    }
  };

  // Drag-to-adjust pelvis pins (works in all captured views)
  const dragKeyRef = useRef<'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS' | null>(null);
  const dragRafRef = useRef<number | null>(null);
  const dragLastPointRef = useRef<{ x: number; y: number } | null>(null);
  const dragPelvisLineTranslateLastImgRef = useRef<{ x: number; y: number } | null>(null);
  const pelvisLineTranslateAccumRef = useRef<{ dx: number; dy: number }>({ dx: 0, dy: 0 });
  const pelvisLineTranslateRafRef = useRef<number | null>(null);
  const dragPoseIndexRef = useRef<number | null>(null);
  /** Image-space offset from pointer to landmark at pointerdown; inner snap zone uses (0,0) so drag tracks from joint center. */
  const poseDragGrabOffsetRef = useRef<{ x: number; y: number } | null>(null);
  const poseDragRafRef = useRef<number | null>(null);
  const panDragRef = useRef<{ pointerId: number; last: { x: number; y: number } } | null>(null);
  const [isPanningCanvas, setIsPanningCanvas] = useState(false);
  const fullscreenZoomRef = useRef(1);
  const fullscreenPanRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });
  const fullscreenTouchPointsRef = useRef<Map<number, { x: number; y: number }>>(new Map());
  const fullscreenPinchStartRef = useRef<{
    distance: number;
    zoom: number;
    pan: { x: number; y: number };
    center: { x: number; y: number };
    anchorImg: { x: number; y: number } | null;
  } | null>(null);

  const clampFullscreenZoom = useCallback((z: number) => Math.min(4, Math.max(1, z)), []);

  const clampFullscreenPan = useCallback((pan: { x: number; y: number }, zoom: number, canvas: HTMLCanvasElement | null) => {
    if (!canvas || zoom <= 1) return { x: 0, y: 0 };
    const maxX = (canvas.width * (zoom - 1)) / 2;
    const maxY = (canvas.height * (zoom - 1)) / 2;
    return {
      x: Math.max(-maxX, Math.min(maxX, pan.x)),
      y: Math.max(-maxY, Math.min(maxY, pan.y)),
    };
  }, []);

  const applyFullscreenViewport = useCallback(
    (zoom: number, pan: { x: number; y: number }, canvas: HTMLCanvasElement | null) => {
      const clampedZoom = clampFullscreenZoom(zoom);
      const clampedPan = clampFullscreenPan(pan, clampedZoom, canvas);
      fullscreenZoomRef.current = clampedZoom;
      fullscreenPanRef.current = clampedPan;
      setFullscreenZoom(clampedZoom);
      setFullscreenPan(clampedPan);
    },
    [clampFullscreenPan, clampFullscreenZoom]
  );

  /*
   * S4/U4: keyboard operability. Every callback below routes into the same
   * `applyFullscreenViewport` / capture paths the pointer handlers use — no new geometry, and
   * nothing that could change what an analysis reports.
   */
  const handleKeyboardZoomIn = useCallback(() => {
    applyFullscreenViewport(
      fullscreenZoomRef.current + 0.25,
      fullscreenPanRef.current,
      fullscreenCanvasRef.current
    );
  }, [applyFullscreenViewport]);

  const handleKeyboardZoomOut = useCallback(() => {
    applyFullscreenViewport(
      fullscreenZoomRef.current - 0.25,
      fullscreenPanRef.current,
      fullscreenCanvasRef.current
    );
  }, [applyFullscreenViewport]);

  const handleKeyboardPan = useCallback(
    ({ dx, dy }: { dx: number; dy: number }) => {
      applyFullscreenViewport(
        fullscreenZoomRef.current,
        { x: fullscreenPanRef.current.x + dx, y: fullscreenPanRef.current.y + dy },
        fullscreenCanvasRef.current
      );
    },
    [applyFullscreenViewport]
  );

  useFullscreenEditorKeyboard({
    isFullScreenOpen,
    onZoomIn: handleKeyboardZoomIn,
    onZoomOut: handleKeyboardZoomOut,
    onPan: handleKeyboardPan,
  });

  const getPelvisPinsForView = useCallback((): Array<{ key: 'ASIS' | 'PSIS' | 'L_ASIS' | 'R_ASIS' | 'L_PSIS' | 'R_PSIS'; p: { x: number; y: number } }> => {
    if (!currentView) return [];
    const img = capturedImages[currentView];
    const custom = img?.custom;
    if (!custom) return [];
    if (currentView === 'Anterior') {
      return [
        ...(custom.L_ASIS ? [{ key: 'L_ASIS' as const, p: custom.L_ASIS }] : []),
        ...(custom.R_ASIS ? [{ key: 'R_ASIS' as const, p: custom.R_ASIS }] : []),
      ];
    }
    if (currentView === 'Posterior') {
      return [
        ...(custom.L_PSIS ? [{ key: 'L_PSIS' as const, p: custom.L_PSIS }] : []),
        ...(custom.R_PSIS ? [{ key: 'R_PSIS' as const, p: custom.R_PSIS }] : []),
      ];
    }
    // Left/Right
    return [
      ...(custom.ASIS ? [{ key: 'ASIS' as const, p: custom.ASIS }] : []),
      ...(custom.PSIS ? [{ key: 'PSIS' as const, p: custom.PSIS }] : []),
    ];
  }, [capturedImages, currentView]);

  const toCanvasPoint = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget as HTMLCanvasElement;
    if (!canvas) return null;
    return clientToCanvasBitmapPoint(e.clientX, e.clientY, canvas);
  };

  /** Convert canvas pixel to image-space point using last draw transform (for pose landmark hit-test/drag). Uses current canvas dimensions so hit-test matches displayed content. */
  const canvasToImagePoint = useCallback((canvasPt: { x: number; y: number }, canvasEl: HTMLCanvasElement | null) => {
    if (!canvasEl) return null;
    const t = canvasToImageTransformMapRef.current.get(canvasEl);
    if (!t || !canvasEl) return null;
    const { centerX, centerY, imgCenterX, imgCenterY, zoomScale, rotationDeg } = t;
    const rad = (rotationDeg * Math.PI) / 180;
    const dx = canvasPt.x - centerX;
    const dy = canvasPt.y - centerY;
    const cos = Math.cos(-rad);
    const sin = Math.sin(-rad);
    const rx = dx * cos - dy * sin;
    const ry = dx * sin + dy * cos;
    return {
      x: rx / zoomScale + imgCenterX,
      y: ry / zoomScale + imgCenterY,
    };
  }, []);

  /** Squared distance from point (px,py) to line segment (ax,ay)-(bx,by). */
  const distToSegmentSq = useCallback((px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
    const dx = bx - ax;
    const dy = by - ay;
    const len2 = dx * dx + dy * dy;
    if (len2 === 0) return (px - ax) ** 2 + (py - ay) ** 2;
    let t = ((px - ax) * dx + (py - ay) * dy) / len2;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx;
    const qy = ay + t * dy;
    return (px - qx) ** 2 + (py - qy) ** 2;
  }, []);

  const handleCanvasPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!currentView || isCameraActive) return;
    const canvas = e.currentTarget as HTMLCanvasElement;
    const pt = toCanvasPoint(e);
    if (!canvas || !pt) return;
    const isFullscreenTouch = canvas === fullscreenCanvasRef.current && e.pointerType === 'touch';
    if (isFullscreenTouch) {
      fullscreenTouchPointsRef.current.set(e.pointerId, pt);
      if (fullscreenTouchPointsRef.current.size >= 2) {
        const pts = Array.from(fullscreenTouchPointsRef.current.values());
        const a = pts[0];
        const b = pts[1];
        const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        fullscreenPinchStartRef.current = {
          distance: Math.max(1, distance),
          zoom: fullscreenZoomRef.current,
          pan: fullscreenPanRef.current,
          center,
          anchorImg: canvasToImagePoint(center, canvas),
        };
        panDragRef.current = null;
        setIsPanningCanvas(false);
        dragKeyRef.current = null;
        dragPoseIndexRef.current = null;
        dragPelvisLineTranslateLastImgRef.current = null;
        setActiveDragPinKey(null);
        setActiveDragPoseIndex(null);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    const imgPt = canvasToImagePoint(pt, canvas);
    const pelvisPinMobile = mobileEditMode;
    const hitR = getPelvisPinAcquireRadiusPx(canvas.width, { mobile: pelvisPinMobile });
    const hitR2 = hitR * hitR;

    const handleHitR = Math.max(16, canvas.width * 0.02);
    const currentLandmarks = capturedImages[currentView]?.landmarks || [];
    if (currentLandmarks.length > 0) {
      const anchor = plumbAnchor(
        currentView,
        currentLandmarks,
        canvas.width,
        canvas.height,
        currentAnchorOffsetPx
      );
      const controls = getPlumbEdgeControlPoints(
        anchor,
        currentImage?.floorCorrectionDeg ?? 0,
        canvas.width,
        canvas.height
      );
      const topD2 = (controls.top.x - pt.x) ** 2 + (controls.top.y - pt.y) ** 2;
      const bottomD2 = (controls.bottom.x - pt.x) ** 2 + (controls.bottom.y - pt.y) ** 2;
      const hitTop = topD2 <= handleHitR * handleHitR;
      const hitBottom = bottomD2 <= handleHitR * handleHitR;
      if (hitTop || hitBottom) {
        const edge: 'top' | 'bottom' = hitTop && hitBottom
          ? (topD2 <= bottomD2 ? 'top' : 'bottom')
          : (hitTop ? 'top' : 'bottom');
        clearPlumbAutoExitTimer();
        setIsManualPlumbMode(true);
        setVisibleCenterMessage('PL edit: drag top/bottom handles');
        plumbHandleDragRef.current = {
          pointerId: e.pointerId,
          edge,
        };
        try {
          (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }

    const pins = getPelvisPinsForView();
    let bestPin: { key: typeof pins[number]['key']; d2: number } | null = null;
    if (pins.length > 0 && imgPt) {
      for (const pin of pins) {
        const dx = pin.p.x - imgPt.x;
        const dy = pin.p.y - imgPt.y;
        const d2 = dx * dx + dy * dy;
        if (d2 <= hitR2 && (!bestPin || d2 < bestPin.d2)) bestPin = { key: pin.key, d2 };
      }
    }

    if (!bestPin && imgPt && onTranslatePelvisRedLine) {
      const custom = capturedImages[currentView]?.custom;
      const segment = getStablePelvisRedLine(currentView, custom);
      const keys = getPelvisRedLinePinKeys(currentView, custom);
      if (segment && keys && custom) {
        const lineHalf = getPelvisRedLineGrabHalfWidthPx(canvas.width, { mobile: pelvisPinMobile });
        const lineHalf2 = lineHalf * lineHalf;
        const dLine = distToSegmentSq(
          imgPt.x,
          imgPt.y,
          segment.start.x,
          segment.start.y,
          segment.end.x,
          segment.end.y
        );
        if (dLine <= lineHalf2) {
          const segDx = segment.end.x - segment.start.x;
          const segDy = segment.end.y - segment.start.y;
          const len2 = segDx * segDx + segDy * segDy;
          if (len2 >= 64) {
            const t = closestPointOnSegmentT(
              imgPt.x,
              imgPt.y,
              segment.start.x,
              segment.start.y,
              segment.end.x,
              segment.end.y
            );
            const midGrab = t >= 0.28 && t <= 0.72;
            if (midGrab) {
              onEditGestureStart?.();
              dragPelvisLineTranslateLastImgRef.current = { x: imgPt.x, y: imgPt.y };
              pelvisLineTranslateAccumRef.current = { dx: 0, dy: 0 };
              dragKeyRef.current = null;
              setActiveDragPinKey(null);
              setActiveDragPoseIndex(null);
              mobilePinEmptyDownRef.current = null;
              mobilePoseEmptyDownRef.current = null;
              try {
                (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
              } catch {
                // ignore
              }
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            const pA = custom[keys[0]];
            const pB = custom[keys[1]];
            if (pA && pB) {
              const dStartA = (segment.start.x - pA.x) ** 2 + (segment.start.y - pA.y) ** 2;
              const keyAtStart = dStartA <= 9 ? keys[0] : keys[1];
              const keyAtEnd = keyAtStart === keys[0] ? keys[1] : keys[0];
              const keyToDrag = t <= 0.5 ? keyAtStart : keyAtEnd;
              bestPin = { key: keyToDrag, d2: dLine };
            }
          }
        }
      }
    }

    if (bestPin) {
      setHoveredPelvisLine(false);
      if (mobileEditMode) {
        if (mobileStickyPinKeyRef.current !== bestPin.key) {
          setMobileStickyPinKey(bestPin.key);
          mobileStickyPinKeyRef.current = bestPin.key;
          setMobileStickyPoseIndex(null);
          mobileStickyPoseIndexRef.current = null;
          mobilePoseEmptyDownRef.current = null;
          mobilePinEmptyDownRef.current = null;
          e.preventDefault();
          e.stopPropagation();
          return;
        }
        onEditGestureStart?.();
        dragKeyRef.current = bestPin.key;
        dragLastPointRef.current = imgPt;
        setActiveDragPinKey(bestPin.key);
        setActiveDragPoseIndex(null);
        mobilePinEmptyDownRef.current = null;
        try {
          (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onEditGestureStart?.();
      dragKeyRef.current = bestPin.key;
      dragLastPointRef.current = imgPt;
      setActiveDragPinKey(bestPin.key);
      setActiveDragPoseIndex(null);
      if (canvas === fullscreenCanvasRef.current && isFullscreenMagnifierEnabled) {
        const pinImg = pins.find((p) => p.key === bestPin.key)?.p;
        if (pinImg) {
          setFullscreenMagnifierImagePoint({ x: pinImg.x, y: pinImg.y });
          setShowFullscreenMagnifier(true);
        }
      }
      try {
        (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (onSetPoseLandmark && currentView) {
      const img = capturedImages[currentView];
      const landmarks = img?.status === 'complete' ? img?.landmarks : undefined;
      if (landmarks && Array.isArray(landmarks) && landmarks.length > 0) {
        const imgPt = canvasToImagePoint(pt, canvas);
        if (imgPt) {
          const hitRImg = getPoseLandmarkAcquireRadiusPx(canvas.width, { mobile: mobileEditMode });
          const hitRImg2 = hitRImg * hitRImg;
          let bestIdx: number | null = null;
          let bestD2 = Infinity;
          const indicesToCheck = getPoseOverlayLandmarkIndices(currentView);
          for (const i of indicesToCheck) {
            const lm = landmarks[i];
            if (!lm) continue;
            const dx = lm.x - imgPt.x;
            const dy = lm.y - imgPt.y;
            const d2 = dx * dx + dy * dy;
            if (d2 <= hitRImg2 && d2 < bestD2) {
              bestD2 = d2;
              bestIdx = i;
            }
          }
          if (bestIdx !== null) {
            const lm = landmarks[bestIdx]!;
            const snapFrac = mobileEditMode ? POSE_LANDMARK_SNAP_TO_CENTER_FRAC_MOBILE : POSE_LANDMARK_SNAP_TO_CENTER_FRAC;
            if (mobileEditMode) {
              if (mobileStickyPoseIndexRef.current !== bestIdx) {
                setMobileStickyPoseIndex(bestIdx);
                mobileStickyPoseIndexRef.current = bestIdx;
                setMobileStickyPinKey(null);
                mobileStickyPinKeyRef.current = null;
                mobilePoseEmptyDownRef.current = null;
                mobilePinEmptyDownRef.current = null;
                e.preventDefault();
                e.stopPropagation();
                return;
              }
              onEditGestureStart?.();
              poseDragGrabOffsetRef.current = computePoseDragGrabOffset(imgPt, lm, hitRImg, snapFrac);
              dragPoseIndexRef.current = bestIdx;
              dragLastPointRef.current = imgPt;
              setActiveDragPoseIndex(bestIdx);
              setActiveDragPinKey(null);
              setHoveredLandmarkIndex(bestIdx);
              setHoveredLineSegment(null);
              mobilePoseEmptyDownRef.current = null;
              try {
                (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
              } catch {
                // ignore
              }
              e.preventDefault();
              e.stopPropagation();
              return;
            }
            onEditGestureStart?.();
            poseDragGrabOffsetRef.current = computePoseDragGrabOffset(imgPt, lm, hitRImg, snapFrac);
            dragPoseIndexRef.current = bestIdx;
            dragLastPointRef.current = imgPt;
            setActiveDragPoseIndex(bestIdx);
            setActiveDragPinKey(null);
            setHoveredLandmarkIndex(bestIdx);
            setHoveredLineSegment(null);
            if (canvas === fullscreenCanvasRef.current && isFullscreenMagnifierEnabled) {
              const g = poseDragGrabOffsetRef.current;
              setFullscreenMagnifierImagePoint({
                x: imgPt.x - g.x,
                y: imgPt.y - g.y,
              });
              setShowFullscreenMagnifier(true);
            }
            try {
              (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            } catch {
              // ignore
            }
            e.preventDefault();
            e.stopPropagation();
            return;
          }
        }
      }
    }

    // Fullscreen pan when zoomed and pointer is on empty space (mouse, pen, or one-finger touch).
    if (
      canvas === fullscreenCanvasRef.current &&
      fullscreenZoomRef.current > 1 &&
      (e.pointerType === 'mouse' || e.pointerType === 'pen' || e.pointerType === 'touch')
    ) {
      panDragRef.current = { pointerId: e.pointerId, last: pt };
      setIsPanningCanvas(true);
      try {
        (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
      } catch {
        // ignore
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }

    if (mobileEditMode && mobileStickyPinKeyRef.current) {
      mobilePinEmptyDownRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        pointerId: e.pointerId,
      };
      e.preventDefault();
      e.stopPropagation();
    }
    if (mobileEditMode && mobileStickyPoseIndexRef.current !== null) {
      mobilePoseEmptyDownRef.current = {
        clientX: e.clientX,
        clientY: e.clientY,
        pointerId: e.pointerId,
      };
      e.preventDefault();
      e.stopPropagation();
    }
  };

  const flushDrag = () => {
    if (!dragKeyRef.current || !dragLastPointRef.current || !currentView) return;
    onSetCustomLandmark(currentView, snapCustomPin(dragKeyRef.current, dragLastPointRef.current), dragKeyRef.current);
  };

  const handleCanvasPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget as HTMLCanvasElement;
    if (canvas === fullscreenCanvasRef.current && e.pointerType === 'touch') {
      const pt = toCanvasPoint(e);
      if (pt && fullscreenTouchPointsRef.current.has(e.pointerId)) {
        fullscreenTouchPointsRef.current.set(e.pointerId, pt);
      }
      const pinch = fullscreenPinchStartRef.current;
      if (pinch && fullscreenTouchPointsRef.current.size >= 2) {
        const pts = Array.from(fullscreenTouchPointsRef.current.values());
        const a = pts[0];
        const b = pts[1];
        const center = { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
        const distance = Math.hypot(a.x - b.x, a.y - b.y);
        const nextZoom = clampFullscreenZoom(pinch.zoom * (distance / Math.max(1, pinch.distance)));
        const imgCenterX = canvas.width / 2;
        const imgCenterY = canvas.height / 2;
        const nextPan = pinch.anchorImg
          ? {
              x: center.x - canvas.width / 2 - (pinch.anchorImg.x - imgCenterX) * nextZoom,
              y: center.y - canvas.height / 2 - (pinch.anchorImg.y - imgCenterY) * nextZoom,
            }
          : {
              x: pinch.pan.x + (center.x - pinch.center.x),
              y: pinch.pan.y + (center.y - pinch.center.y),
            };
        applyFullscreenViewport(nextZoom, nextPan, canvas);
        e.preventDefault();
        e.stopPropagation();
        return;
      }
    }
    if (
      canvas === fullscreenCanvasRef.current &&
      panDragRef.current &&
      panDragRef.current.pointerId === e.pointerId
    ) {
      const pt = toCanvasPoint(e);
      if (!pt) return;
      const prev = panDragRef.current.last;
      const dx = pt.x - prev.x;
      const dy = pt.y - prev.y;
      panDragRef.current.last = pt;
      const pan = fullscreenPanRef.current;
      applyFullscreenViewport(fullscreenZoomRef.current, { x: pan.x + dx, y: pan.y + dy }, canvas);
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    if (
      plumbHandleDragRef.current &&
      plumbHandleDragRef.current.pointerId === e.pointerId &&
      currentView &&
      onManualPlumbUpdate
    ) {
      const pt = toCanvasPoint(e);
      const currentLandmarks = capturedImages[currentView]?.landmarks || [];
      if (pt && currentLandmarks.length > 0) {
        const anchor = plumbAnchor(
          currentView,
          currentLandmarks,
          canvas.width,
          canvas.height,
          currentAnchorOffsetPx
        );
        const controls = getPlumbEdgeControlPoints(
          anchor,
          currentImage?.floorCorrectionDeg ?? 0,
          canvas.width,
          canvas.height
        );
        const edgeY = plumbHandleDragRef.current.edge === 'top' ? controls.top.y : controls.bottom.y;
        const margin = Math.max(20, Math.min(canvas.width, canvas.height) * 0.04);
        const constrainedPoint = {
          x: Math.max(margin, Math.min(canvas.width - margin, pt.x)),
          y: edgeY,
        };
        const vx = constrainedPoint.x - anchor.x;
        const vy = constrainedPoint.y - anchor.y;
        if (captureSettings.plumblineSnap && Math.abs(vx) <= clampSnapRadius(captureSettings.plumblineSnapRadius, PLUMB_SNAP_DEFAULT_PX)) {
          // Snap the plumb back to true vertical (floor correction = 0).
          onManualPlumbUpdate(currentView, {
            manualCorrectionDeg: 0,
            plControlPoint: { x: anchor.x, y: constrainedPoint.y },
          });
        } else if (Math.hypot(vx, vy) > 8) {
          const angleFromVertical = (Math.atan2(vx, -vy) * 180) / Math.PI;
          const manualCorrectionDeg = normalizeFloorAngleDeg(-angleFromVertical);
          onManualPlumbUpdate(currentView, {
            manualCorrectionDeg,
            plControlPoint: constrainedPoint,
          });
        }
      }
      e.preventDefault();
      e.stopPropagation();
      return;
    }
    const updateFullscreenMagnifier = () => {
      if (canvas !== fullscreenCanvasRef.current || !isFullscreenMagnifierEnabled) return;
      const pt = toCanvasPoint(e);
      if (!pt) return;
      const imgAtPointer = canvasToImagePoint(pt, canvas);
      if (!imgAtPointer) return;
      if (dragPoseIndexRef.current !== null && poseDragGrabOffsetRef.current) {
        const g = poseDragGrabOffsetRef.current;
        setFullscreenMagnifierImagePoint({
          x: imgAtPointer.x - g.x,
          y: imgAtPointer.y - g.y,
        });
      } else {
        setFullscreenMagnifierImagePoint({ x: imgAtPointer.x, y: imgAtPointer.y });
      }
      setShowFullscreenMagnifier(true);
    };

    if (
      dragPelvisLineTranslateLastImgRef.current !== null &&
      onTranslatePelvisRedLine &&
      currentView &&
      !isCameraActive
    ) {
      const pt = toCanvasPoint(e);
      const imgPt = pt ? canvasToImagePoint(pt, canvas) : null;
      const last = dragPelvisLineTranslateLastImgRef.current;
      if (imgPt && last) {
        updateFullscreenMagnifier();
        const dx = imgPt.x - last.x;
        const dy = imgPt.y - last.y;
        dragPelvisLineTranslateLastImgRef.current = { x: imgPt.x, y: imgPt.y };
        if (dx * dx + dy * dy > 1e-6) {
          const acc = pelvisLineTranslateAccumRef.current;
          acc.dx += dx;
          acc.dy += dy;
          if (pelvisLineTranslateRafRef.current === null) {
            pelvisLineTranslateRafRef.current = window.requestAnimationFrame(() => {
              pelvisLineTranslateRafRef.current = null;
              const a = pelvisLineTranslateAccumRef.current;
              const adx = a.dx;
              const ady = a.dy;
              a.dx = 0;
              a.dy = 0;
              if ((adx || ady) && currentView) {
                onTranslatePelvisRedLine(currentView, { dx: adx, dy: ady });
              }
            });
          }
        }
        e.preventDefault();
      }
      return;
    }

    if (
      mobileEditMode &&
      canvas &&
      dragPoseIndexRef.current === null &&
      dragKeyRef.current === null &&
      dragPelvisLineTranslateLastImgRef.current === null
    ) {
      const slop2 = MOBILE_POSE_DRAG_SLOP_PX * MOBILE_POSE_DRAG_SLOP_PX;

      const promotePinFromEmpty = (): boolean => {
        const pending = mobilePinEmptyDownRef.current;
        if (!pending || pending.pointerId !== e.pointerId) return false;
        const dx = e.clientX - pending.clientX;
        const dy = e.clientY - pending.clientY;
        if (dx * dx + dy * dy < slop2) return false;
        const k = mobileStickyPinKeyRef.current;
        if (!k || !currentView) return false;
        const pt = toCanvasPoint(e);
        const imgPt = pt ? canvasToImagePoint(pt, canvas) : null;
        if (!imgPt) return false;
        onEditGestureStart?.();
        dragKeyRef.current = k;
        dragLastPointRef.current = imgPt;
        setActiveDragPinKey(k);
        setActiveDragPoseIndex(null);
        mobilePinEmptyDownRef.current = null;
        mobilePoseEmptyDownRef.current = null;
        try {
          (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        e.preventDefault();
        return true;
      };

      const promotePoseFromEmpty = (): boolean => {
        const pending = mobilePoseEmptyDownRef.current;
        if (!pending || pending.pointerId !== e.pointerId) return false;
        const dx = e.clientX - pending.clientX;
        const dy = e.clientY - pending.clientY;
        if (dx * dx + dy * dy < slop2) return false;
        const idx = mobileStickyPoseIndexRef.current;
        if (idx === null || !onSetPoseLandmark || !currentView) return false;
        const pt = toCanvasPoint(e);
        const imgPt = pt ? canvasToImagePoint(pt, canvas) : null;
        const img = capturedImages[currentView];
        const landmarks = img?.status === 'complete' ? img?.landmarks : undefined;
        if (!landmarks || !imgPt) return false;
        const lm = landmarks[idx];
        if (!lm) return false;
        const hitRImg = getPoseLandmarkAcquireRadiusPx(canvas.width, { mobile: true });
        onEditGestureStart?.();
        poseDragGrabOffsetRef.current = computePoseDragGrabOffset(
          imgPt,
          lm,
          hitRImg,
          POSE_LANDMARK_SNAP_TO_CENTER_FRAC_MOBILE
        );
        dragPoseIndexRef.current = idx;
        dragLastPointRef.current = imgPt;
        setActiveDragPoseIndex(idx);
        setActiveDragPinKey(null);
        setHoveredLandmarkIndex(idx);
        setHoveredLineSegment(null);
        mobilePoseEmptyDownRef.current = null;
        mobilePinEmptyDownRef.current = null;
        try {
          (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
        } catch {
          // ignore
        }
        e.preventDefault();
        return true;
      };

      if (promotePinFromEmpty()) {
        return;
      }
      if (promotePoseFromEmpty()) {
        return;
      }
    }

    if (dragPoseIndexRef.current !== null && onSetPoseLandmark && currentView) {
      const pt = toCanvasPoint(e);
      const imgPt = pt ? canvasToImagePoint(pt, e.currentTarget as HTMLCanvasElement) : null;
      const grab = poseDragGrabOffsetRef.current;
      if (imgPt && grab) {
        updateFullscreenMagnifier();
        if (poseDragRafRef.current) return;
        poseDragRafRef.current = window.requestAnimationFrame(() => {
          poseDragRafRef.current = null;
          const snapped = snapPoseLandmark(dragPoseIndexRef.current!, {
            x: imgPt.x - grab.x,
            y: imgPt.y - grab.y,
          });
          onSetPoseLandmark(currentView, dragPoseIndexRef.current!, snapped);
        });
        e.preventDefault();
      }
      return;
    }
    if (!dragKeyRef.current && !dragPoseIndexRef.current && currentView && !mobileEditMode) {
      const img = capturedImages[currentView];
      const landmarks = img?.status === 'complete' ? img?.landmarks : undefined;
      const pt = toCanvasPoint(e);
      const imgPt = pt ? canvasToImagePoint(pt, canvas) : null;
      if (imgPt) {
        const pelvisHoverR = getPelvisPinHoverMagnetRadiusPx(canvas?.width ?? 0, { mobile: false });
        const pelvisHoverR2 = pelvisHoverR * pelvisHoverR;
        const pins = getPelvisPinsForView();
        let bestPin: { key: PelvisPinKey; d2: number } | null = null;
        for (const pin of pins) {
          const d2 = (pin.p.x - imgPt.x) ** 2 + (pin.p.y - imgPt.y) ** 2;
          if (d2 <= pelvisHoverR2 && (!bestPin || d2 < bestPin.d2)) {
            bestPin = { key: pin.key, d2 };
          }
        }
        if (bestPin) {
          setHoveredPelvisPinKey(bestPin.key);
          setHoveredPelvisLine(false);
          setHoveredLandmarkIndex(null);
          setHoveredLineSegment(null);
          return;
        }
        setHoveredPelvisPinKey(null);
        const segment = getStablePelvisRedLine(currentView, img?.custom);
        if (segment) {
          const lineHalf = getPelvisRedLineGrabHalfWidthPx(canvas.width, { mobile: false });
          const lineD2 = distToSegmentSq(
            imgPt.x,
            imgPt.y,
            segment.start.x,
            segment.start.y,
            segment.end.x,
            segment.end.y
          );
          if (lineD2 <= lineHalf * lineHalf) {
            setHoveredPelvisLine(true);
            setHoveredLandmarkIndex(null);
            setHoveredLineSegment(null);
            return;
          }
        }
        setHoveredPelvisLine(false);

        if (!(onSetPoseLandmark && landmarks && Array.isArray(landmarks))) {
          setHoveredLandmarkIndex(null);
          setHoveredLineSegment(null);
          return;
        }

        const hitRImg = getPoseLandmarkHoverMagnetRadiusPx(canvas?.width ?? 0, { mobile: false });
        const hitRImg2 = hitRImg * hitRImg;
        let bestIdx: number | null = null;
        let bestD2 = Infinity;
        const indicesToCheck = getPoseOverlayLandmarkIndices(currentView);
        for (const i of indicesToCheck) {
          const lm = landmarks[i];
          if (!lm) continue;
          const d2 = (lm.x - imgPt.x) ** 2 + (lm.y - imgPt.y) ** 2;
          if (d2 <= hitRImg2 && d2 < bestD2) {
            bestD2 = d2;
            bestIdx = i;
          }
        }
        if (bestIdx !== null) {
          setHoveredLandmarkIndex(bestIdx);
          setHoveredLineSegment(null);
          setHoveredPelvisPinKey(null);
          setHoveredPelvisLine(false);
          return;
        }
        const lineHitThreshold = canvas ? Math.max(16, canvas.width * 0.015) : 16;
        const lineHitThreshold2 = lineHitThreshold * lineHitThreshold;
        const segments = currentView
          ? getSegmentsForView(currentView)
          : currentView === 'Anterior' || currentView === 'Posterior'
            ? [
                [IDX.L_SHO, IDX.R_SHO], [IDX.L_HIP, IDX.R_HIP],
                [IDX.L_SHO, IDX.L_HIP], [IDX.R_SHO, IDX.R_HIP],
                [IDX.L_HIP, IDX.L_KNEE], [IDX.L_KNEE, IDX.L_ANK],
                [IDX.R_HIP, IDX.R_KNEE], [IDX.R_KNEE, IDX.R_ANK],
              ] as [number, number][]
            : currentView === 'Left'
              ? [[IDX.L_EAR, IDX.L_SHO], [IDX.L_SHO, IDX.L_HIP], [IDX.L_HIP, IDX.L_KNEE], [IDX.L_KNEE, IDX.L_ANK]] as [number, number][]
              : [[IDX.R_EAR, IDX.R_SHO], [IDX.R_SHO, IDX.R_HIP], [IDX.R_HIP, IDX.R_KNEE], [IDX.R_KNEE, IDX.R_ANK]] as [number, number][];
        let bestSeg: [number, number] | null = null;
        let bestSegD2 = lineHitThreshold2;
        for (const [i, j] of segments) {
          const a = landmarks[i];
          const b = landmarks[j];
          if (!a || !b) continue;
          const d2 = distToSegmentSq(imgPt.x, imgPt.y, a.x, a.y, b.x, b.y);
          if (d2 < bestSegD2) {
            bestSegD2 = d2;
            bestSeg = [i, j];
          }
        }
        if (bestSeg) {
          setHoveredLineSegment(bestSeg);
          setHoveredLandmarkIndex(null);
          setHoveredPelvisPinKey(null);
          setHoveredPelvisLine(false);
        } else {
          setHoveredLandmarkIndex(null);
          setHoveredLineSegment(null);
          setHoveredPelvisPinKey(null);
          setHoveredPelvisLine(false);
        }
      } else {
        setHoveredLandmarkIndex(null);
        setHoveredLineSegment(null);
        setHoveredPelvisPinKey(null);
        setHoveredPelvisLine(false);
      }
    }
    if (!dragKeyRef.current) return;
    const pt = toCanvasPoint(e);
    const imgPt = pt ? canvasToImagePoint(pt, canvas) : null;
    if (!imgPt || !currentView) return;
    updateFullscreenMagnifier();
    dragLastPointRef.current = imgPt;
    if (dragRafRef.current) return;
    dragRafRef.current = window.requestAnimationFrame(() => {
      dragRafRef.current = null;
      flushDrag();
    });
    e.preventDefault();
  };

  const handleCanvasPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (panDragRef.current && panDragRef.current.pointerId === e.pointerId) {
      panDragRef.current = null;
      setIsPanningCanvas(false);
    }
    if (e.currentTarget === fullscreenCanvasRef.current && e.pointerType === 'touch') {
      fullscreenTouchPointsRef.current.delete(e.pointerId);
      if (fullscreenTouchPointsRef.current.size < 2) {
        fullscreenPinchStartRef.current = null;
      }
    }
    if (pelvisLineTranslateRafRef.current !== null) {
      window.cancelAnimationFrame(pelvisLineTranslateRafRef.current);
      pelvisLineTranslateRafRef.current = null;
    }
    const acc = pelvisLineTranslateAccumRef.current;
    if ((acc.dx || acc.dy) && currentView && onTranslatePelvisRedLine) {
      onTranslatePelvisRedLine(currentView, { dx: acc.dx, dy: acc.dy });
    }
    acc.dx = 0;
    acc.dy = 0;
    dragPelvisLineTranslateLastImgRef.current = null;
    if (plumbHandleDragRef.current?.pointerId === e.pointerId) {
      plumbHandleDragRef.current = null;
      schedulePlumbAutoExit();
    }
    if (mobileEditMode) {
      if (mobilePoseEmptyDownRef.current && mobilePoseEmptyDownRef.current.pointerId === e.pointerId) {
        mobilePoseEmptyDownRef.current = null;
        setMobileStickyPoseIndex(null);
        mobileStickyPoseIndexRef.current = null;
      }
      if (mobilePinEmptyDownRef.current && mobilePinEmptyDownRef.current.pointerId === e.pointerId) {
        mobilePinEmptyDownRef.current = null;
        setMobileStickyPinKey(null);
        mobileStickyPinKeyRef.current = null;
      }
    }
    if (poseDragRafRef.current) {
      window.cancelAnimationFrame(poseDragRafRef.current);
      poseDragRafRef.current = null;
    }
    if (dragPoseIndexRef.current !== null) {
      dragPoseIndexRef.current = null;
    }
    poseDragGrabOffsetRef.current = null;
    if (dragRafRef.current) {
      window.cancelAnimationFrame(dragRafRef.current);
      dragRafRef.current = null;
    }
    if (dragKeyRef.current) {
      flushDrag();
    }
    dragKeyRef.current = null;
    dragLastPointRef.current = null;
    setActiveDragPoseIndex(null);
    setActiveDragPinKey(null);
    if (!mobileEditMode) {
      setHoveredLandmarkIndex(null);
      setHoveredLineSegment(null);
      setHoveredPelvisPinKey(null);
      setHoveredPelvisLine(false);
    }
    if (e.currentTarget === fullscreenCanvasRef.current) {
      setShowFullscreenMagnifier(false);
      setFullscreenMagnifierImagePoint(null);
    }
  };

  const handleCanvasPointerLeave = () => {
    panDragRef.current = null;
    setIsPanningCanvas(false);
    fullscreenPinchStartRef.current = null;
    fullscreenTouchPointsRef.current.clear();
    if (!mobileEditMode) {
      setHoveredLandmarkIndex(null);
      setHoveredLineSegment(null);
      setHoveredPelvisPinKey(null);
      setHoveredPelvisLine(false);
    }
    setActiveDragPoseIndex(null);
    setActiveDragPinKey(null);
    setShowFullscreenMagnifier(false);
  };

  const anyViewHasPelvisPins = useMemo(() => {
    const views: CaptureSequence[] = ['Anterior', 'Posterior', 'Left', 'Right'];
    for (const v of views) {
      const c = capturedImages[v]?.custom;
      if (!c) continue;
      if (v === 'Anterior' && (c.L_ASIS || c.R_ASIS)) return true;
      if (v === 'Posterior' && (c.L_PSIS || c.R_PSIS)) return true;
      if ((v === 'Left' || v === 'Right') && (c.ASIS || c.PSIS)) return true;
    }
    return false;
  }, [capturedImages]);

  const isReadyForAnalysis = settings.clientHeightCM > 0;
  const topChromeReductionPx =
    isMobile && isImmersiveChromeEnabled && isPostureExtendedChrome && isChromeHidden
      ? MOBILE_PAGE_HEADER_COLLAPSED_HEIGHT_PX + MOBILE_POSTURE_TOOLBAR_HEIGHT_PX
      : isMobile && isImmersiveChromeEnabled && isChromeHidden
        ? MOBILE_PAGE_HEADER_COLLAPSED_HEIGHT_PX / 2
        : 0;
  const chromeBottomDismissHidden =
    isImmersiveChromeEnabled && isPostureExtendedChrome && isChromeHidden;
  const isSelfAssessment = mode.mode === 'self-assessment';
  const hasAnyCapturedImage = effectiveSequence.some((view) => capturedImages[view]?.imageData);
  /** Fullscreen editor uses its own canvas sizing — do not cap inline Pic height while open. */
  const selfAssessmentEmptyCapPx =
    isSelfAssessment && !hasAnyCapturedImage && !isCameraActive
      ? (isMobile ? SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_PX : SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_DESKTOP_PX)
      : isSelfAssessment && !isMobile && unassignedImages.length > 0 && !isCameraActive
        ? SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_DESKTOP_PX
        : undefined;
  const isSelfAssessmentEmptyDropZone = selfAssessmentEmptyCapPx != null;
  const selfAssessmentEmptyDropZoneHeightPx = isMobile
    ? SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_PX
    : SELF_ASSESSMENT_EMPTY_CAPTURE_HEIGHT_DESKTOP_PX;
  const inlineMaxCanvasHeightPx = isFullScreenOpen
    ? undefined
    : (maxCanvasHeightPx ?? selfAssessmentEmptyCapPx);
  const currentImageAspectRatio = !isCameraActive && currentView ? capturedAspectRatios[currentView] : undefined;
  const compactChrome = Boolean(inlineMaxCanvasHeightPx);
  const preferFullWidthMobileCanvas = (compactChrome || (isSelfAssessment && hasAnyCapturedImage)) && isMobile;
  /**
   * Narrow layout stretches the shell to the screen, then a height cap letterboxes the photo.
   * Once we know the picture's aspect, the media box grows to that width instead.
   */
  const fillPhotoToColumnWidth =
    isMobile && !fillAvailable && !isNativeFullscreen && Boolean(currentImageAspectRatio);
  const photoShellBox = useObservedBox(cameraViewportRef, fillPhotoToColumnWidth);
  const fullWidthPhotoHeightPx =
    fillPhotoToColumnWidth && currentImageAspectRatio
      ? fullWidthMediaHeight(photoShellBox.width, currentImageAspectRatio)
      : 0;
  const compactViewSelectors = compactChrome || isMobile;
  const captureCardClass = isSelfAssessment
    ? 'border-line bg-surface shadow-sm'
    : 'border-cyan-500/20 shadow-md';
  const captureViewportClass = isSelfAssessment
    ? 'bg-surface border border-line'
    : 'bg-muted/30 dark:bg-muted/10';
  /**
   * Docked toolbar strip (S3b — docked in-flow chrome below/above the media frame).
   * Uses theme tokens to blend seamlessly with light, dark, and glass themes.
   */
  const captureToolbarShellClass = 'bg-surface/90 backdrop-blur-md border-line text-fg';
  /** Fullscreen editor chrome — same tokens as the docked toolbar so letterbox and controls follow the active theme. */
  const fullscreenChromeClass = 'border border-line bg-surface text-fg shadow-lg backdrop-blur-md';
  const fullscreenChromeBtnClass =
    'shrink-0 border border-line bg-bg text-fg hover:bg-surface disabled:opacity-40';
  const captureToolbarBtnClass =
    'text-muted hover:text-fg hover:bg-muted/50 disabled:text-muted/40 disabled:opacity-100';
  const captureToolbarSepClass = 'bg-line';
  const captureStartCameraClass = 'bg-primary hover:bg-primary/90 text-primary-foreground';
  const laymanUploadLabel = 'Upload';
  const laymanCameraLabel = isCameraActive
    ? (nextPendingCaptureView ? 'Capture' : 'Finish capture')
    : 'Take photo';
  /** After the first capture, icon-only toolbar buttons avoid clipping on narrow viewports. */
  const laymanToolbarCompact = laymanMode && Boolean(currentImage?.imageData);
  /**
   * S3/U1 — secondary capture tools as descriptors.
   *
   * Primary actions (Upload / Capture / Settings) stay inline at 44 px. Everything else is
   * described here so it can render inline on desktop, in the original positions, or collapse into
   * one labelled overflow on a phone — where ten 28 px icon-only buttons in a `scrollbar-hide`
   * strip previously meant half-size targets and controls that silently scrolled out of reach.
   *
   * Three groups because desktop ordering is preserved exactly: the original strip interleaves
   * secondary tools around the primary buttons, and this pass is not allowed to reshuffle it.
   */
  const hasCurrentCapture = Boolean(currentView && capturedImages[currentView]);
  const showEditTools = hasCurrentCapture && !isCameraActive;

  const handlePelvisPlacementToggle = useCallback(() => {
    if (pelvisPlacementStep === 'none') {
      if (currentView === 'Anterior') {
        setPelvisPlacementStep('L_ASIS');
        setVisibleCenterMessage('Tap Left ASIS (Front of Pelvis)');
      } else if (currentView === 'Posterior') {
        setPelvisPlacementStep('L_PSIS');
        setVisibleCenterMessage('Tap Left PSIS (Back of Pelvis)');
      } else {
        setPelvisPlacementStep('ASIS');
        setVisibleCenterMessage('Tap ASIS (Front of Pelvis)');
      }
    } else {
      setPelvisPlacementStep('none');
      setVisibleCenterMessage(null);
    }
  }, [pelvisPlacementStep, currentView]);

  const leadingSecondaryActions = useMemo<CaptureToolbarAction[]>(() => {
    const actions: CaptureToolbarAction[] = [];

    /*
     * S3b: the dock-position toggle still decides whether the strip sits above or below the frame,
     * but it leaves the live-camera set — it is a layout preference, not something anyone reaches for
     * while a client is holding a pose, and dropping it is part of what keeps that state to four
     * controls with no overflow menu.
     */
    if (!isCameraActive) {
      actions.push({
        id: 'toolbar-position',
        // Practitioners kept the more explicit wording their tooltip already used; the layman
        // strings stay short because the funnel toolbar is narrower.
        label: laymanMode
          ? captureSettings.toolbarPosition === 'top'
            ? 'Move bar down'
            : 'Move bar up'
          : captureSettings.toolbarPosition === 'top'
            ? 'Move Toolbar to Bottom'
            : 'Move Toolbar to Top',
        icon:
          captureSettings.toolbarPosition === 'top' ? (
            <ArrowDown className={CAPTURE_TOOLBAR_ICON_CLASS} />
          ) : (
            <ArrowUp className={CAPTURE_TOOLBAR_ICON_CLASS} />
          ),
        onClick: () =>
          onCaptureSettingsChange({
            ...captureSettings,
            toolbarPosition: captureSettings.toolbarPosition === 'top' ? 'bottom' : 'top',
          }),
      });
    }

    if (showEditTools) {
      actions.push({
        id: 'delete',
        label: 'Delete',
        icon: <TrashIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />,
        onClick: onDeleteView,
        tone: 'destructive',
      });
    }

    if (!laymanMode && showEditTools) {
      actions.push({
        id: 'overlay-toggle',
        label: overlayState[currentView!]?.client ? 'Hide Overlay' : 'Show Overlay',
        icon: overlayState[currentView!]?.client ? (
          <EyeIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />
        ) : (
          <EyeOffIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />
        ),
        onClick: () => onOverlayToggle(currentView!),
        active: Boolean(overlayState[currentView!]?.client),
      });
    }

    if (showEditTools) {
      actions.push({
        id: 'reset-overlay',
        label: laymanMode ? 'Reset alignment lines' : 'Reset overlay alignment',
        icon: <RotateCcw className={CAPTURE_TOOLBAR_ICON_CLASS} />,
        onClick: handleResetOverlay,
      });
    }

    if (!laymanMode && showEditTools && onClearAllPelvisPins && anyViewHasPelvisPins) {
      actions.push({
        id: 'clear-pelvis-pins',
        label: 'Remove ASIS and PSIS markers from all views',
        icon: <PinOff className={CAPTURE_TOOLBAR_ICON_CLASS} />,
        onClick: () => onClearAllPelvisPins(),
      });
    }

    if (!laymanMode && showEditTools) {
      actions.push({
        id: 'pelvis-placement',
        label: pelvisPlacementStep !== 'none' ? 'Cancel Placement' : 'Add Pelvis Landmarks',
        icon:
          pelvisPlacementStep !== 'none' ? (
            <XCircle className={CAPTURE_TOOLBAR_ICON_CLASS} />
          ) : (
            <Target className={CAPTURE_TOOLBAR_ICON_CLASS} />
          ),
        onClick: handlePelvisPlacementToggle,
        active: pelvisPlacementStep !== 'none',
      });
    }

    return actions;
  }, [
    captureSettings,
    onCaptureSettingsChange,
    isCameraActive,
    showEditTools,
    onDeleteView,
    laymanMode,
    overlayState,
    currentView,
    onOverlayToggle,
    handleResetOverlay,
    onClearAllPelvisPins,
    anyViewHasPelvisPins,
    pelvisPlacementStep,
    handlePelvisPlacementToggle,
  ]);

  const cameraSecondaryActions = useMemo<CaptureToolbarAction[]>(() => {
    if (laymanMode || !isCameraActive) return [];
    return [
      {
        id: 'realtime-overlay',
        label: overlayState[nextView as CaptureSequence]?.realtime
          ? 'Hide Real-time Overlay'
          : 'Show Real-time Overlay',
        icon: overlayState[nextView as CaptureSequence]?.realtime ? (
          <EyeIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />
        ) : (
          <EyeOffIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />
        ),
        onClick: () => onRealtimeOverlayToggle(nextView as CaptureSequence),
        active: Boolean(overlayState[nextView as CaptureSequence]?.realtime),
      },
    ];
  }, [laymanMode, isCameraActive, overlayState, nextView, onRealtimeOverlayToggle]);

  const trailingSecondaryActions = useMemo<CaptureToolbarAction[]>(() => {
    const actions: CaptureToolbarAction[] = [];

    if (isCameraActive && videoDevices.length > 1) {
      actions.push({
        id: 'switch-camera',
        label: 'Switch camera',
        icon: <SwitchCameraIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />,
        onClick: switchCamera,
      });
    }

    if (showEditTools) {
      actions.push({
        id: 'fullscreen',
        label: laymanMode ? 'View full screen and zoom' : 'View Full Screen',
        icon: <Maximize2 className={CAPTURE_TOOLBAR_ICON_CLASS} />,
        onClick: () => setIsFullScreenOpen(true),
      });
    }

    return actions;
  }, [isCameraActive, videoDevices.length, switchCamera, showEditTools, laymanMode]);

  /** Collapsed order mirrors the desktop strip so muscle memory survives the switch. */
  const allSecondaryActions = useMemo(
    () => [...leadingSecondaryActions, ...cameraSecondaryActions, ...trailingSecondaryActions],
    [leadingSecondaryActions, cameraSecondaryActions, trailingSecondaryActions]
  );

  /** Mobile split: view tabs sit above the Pic — reserve height so the canvas does not push them off-screen. */
  const pinViewTabsAbovePic = true;
  const viewTabsTopReservePx = pinViewTabsAbovePic
    ? MOBILE_POSTURE_VIEW_TABS_HEIGHT_PX + 6
    : isSelfAssessment && hasAnyCapturedImage
      ? 72
      : 0;
  const effectiveMaxCanvasHeightPx = isSelfAssessment && hasAnyCapturedImage ? undefined : inlineMaxCanvasHeightPx;
  const canvasSize = useCanvasSize(
    0,
    rightReservationPx,
    effectiveMaxCanvasHeightPx,
    topChromeReductionPx,
    viewTabsTopReservePx,
    isSelfAssessment ? 0 : undefined,
    preferFullWidthMobileCanvas,
    currentImageAspectRatio,
  );
  const handleUploadClick = useCallback(() => uploadInputRef.current?.click(), []);

  const stepFullscreenZoom = useCallback((direction: 1 | -1) => {
    const canvas = fullscreenCanvasRef.current;
    if (!canvas) return;
    const center = { x: canvas.width / 2, y: canvas.height / 2 };
    const anchorImg = canvasToImagePoint(center, canvas);
    const currentZoom = fullscreenZoomRef.current;
    const nextZoom = clampFullscreenZoom(currentZoom + direction * 0.2);
    if (!anchorImg) {
      applyFullscreenViewport(nextZoom, fullscreenPanRef.current, canvas);
      return;
    }
    const imgCenterX = canvas.width / 2;
    const imgCenterY = canvas.height / 2;
    const nextPan = {
      x: center.x - canvas.width / 2 - (anchorImg.x - imgCenterX) * nextZoom,
      y: center.y - canvas.height / 2 - (anchorImg.y - imgCenterY) * nextZoom,
    };
    applyFullscreenViewport(nextZoom, nextPan, canvas);
  }, [applyFullscreenViewport, canvasToImagePoint, clampFullscreenZoom]);

  const handleFullscreenWheel = useCallback((e: React.WheelEvent<HTMLCanvasElement>) => {
    const canvas = e.currentTarget;
    const pt = clientToCanvasBitmapPoint(e.clientX, e.clientY, canvas);
    if (!pt) return;
    const anchorImg = canvasToImagePoint(pt, canvas);
    if (!anchorImg) return;
    const currentZoom = fullscreenZoomRef.current;
    const factor = Math.exp(-e.deltaY * 0.0015);
    const nextZoom = clampFullscreenZoom(currentZoom * factor);
    const imgCenterX = canvas.width / 2;
    const imgCenterY = canvas.height / 2;
    const nextPan = {
      x: pt.x - canvas.width / 2 - (anchorImg.x - imgCenterX) * nextZoom,
      y: pt.y - canvas.height / 2 - (anchorImg.y - imgCenterY) * nextZoom,
    };
    applyFullscreenViewport(nextZoom, nextPan, canvas);
    e.preventDefault();
  }, [applyFullscreenViewport, canvasToImagePoint, clampFullscreenZoom]);

  const handleZoomPanelPointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    const target = e.target as HTMLElement;
    if (target.closest('button')) return;
    
    e.preventDefault();
    zoomPanelDragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      offsetX: zoomPanelOffset.x,
      offsetY: zoomPanelOffset.y,
    };
    setIsDraggingZoomPanel(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  }, [zoomPanelOffset]);

  const handleZoomPanelPointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomPanelDragRef.current) return;
    const dx = e.clientX - zoomPanelDragRef.current.startX;
    const dy = e.clientY - zoomPanelDragRef.current.startY;
    setZoomPanelOffset({
      x: zoomPanelDragRef.current.offsetX + dx,
      y: zoomPanelDragRef.current.offsetY + dy,
    });
  }, []);

  const handleZoomPanelPointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!zoomPanelDragRef.current) return;
    e.currentTarget.releasePointerCapture(e.pointerId);
    zoomPanelDragRef.current = null;
    setIsDraggingZoomPanel(false);
  }, []);

  // Expose handlers to parent
  useEffect(() => {
    if (onCaptureHandlersReady) {
      onCaptureHandlersReady({
        isCameraActive,
        isReadyForAnalysis,
        nextCaptureView: nextPendingCaptureView ?? undefined,
        handleCaptureClick,
        handleUploadClick,
        stopCamera
      });
    }
  }, [isCameraActive, isReadyForAnalysis, nextPendingCaptureView, handleCaptureClick, handleUploadClick, onCaptureHandlersReady, stopCamera]);

  const showPractitionerStrip = !(isSelfAssessment && laymanMode);
  /** Desktop column is viewport-sized: the picture fits that box, controls stay pinned. */
  const fitDesktopPhoto = fillAvailable && !isNativeFullscreen;

  const viewSelectorGrid = (
    <div
      className={cn(
        'grid min-w-0 px-0.5 w-full mx-auto shrink-0',
        pinViewTabsAbovePic
          ? 'gap-1 mb-[-6px]'
          : compactViewSelectors
            ? 'gap-1 mt-[-6px]'
            : 'gap-2 mt-[-12px]',
        effectiveSequence.length === 4 ? 'grid-cols-4' : 'grid-cols-3',
        isPostureExtendedChrome && 'chrome-dismiss-transition',
        chromeBottomDismissHidden && 'chrome-dismiss-bottom-hidden',
      )}
      style={
        !fillAvailable && !inlineMaxCanvasHeightPx && canvasSize.width > 0
          ? { maxWidth: `${canvasSize.width}px` }
          : undefined
      }
    >
      {effectiveSequence.map(view => {
        const state = capturedImages[view];
        let statusClass = 'pending';
        let text: string = viewDisplayLabel(view);
        if (state) {
          statusClass = state.status;
          if (state.status === 'failed') text = 'Retry';
          else if (state.status !== 'complete') text = '...';
        }

        const statusStyles: Record<string, string> = {
          pending: isSelfAssessment
            ? 'bg-bg text-fg border border-line hover:bg-surface'
            : 'bg-muted text-muted-foreground hover:bg-muted/80',
          queued: isSelfAssessment
            ? 'bg-status-info-surface text-status-info-text border border-line'
            : 'bg-slate-600 text-white',
          processing: 'bg-blue-600 animate-pulse text-white',
          analyzing: 'bg-violet-600 animate-pulse text-white',
          complete: isSelfAssessment
            ? 'bg-surface text-primary border border-primary hover:bg-primary/5 cursor-pointer'
            : 'bg-primary text-primary-foreground hover:bg-primary/90 cursor-pointer',
          failed: 'bg-destructive text-destructive-foreground hover:bg-destructive/90 cursor-pointer',
        };

        return (
          <Button
            key={view}
            onClick={() => {
              logger.debug('PostureCapture: view tab selected', {
                category: LogCategory.UI,
                view,
                previousView: currentView,
                status: state?.status,
              });

              try {
                if (state?.status === 'complete') {
                  stopCamera();
                  onViewSelect(view);
                }
                if (state?.status === 'failed') {
                  onQueueImage(state.imageData, view);
                }
              } catch (error) {
                logger.error('PostureCapture: view tab click failed', {
                  category: LogCategory.UI,
                  view,
                  error: error instanceof Error ? error.message : String(error),
                });
                throw error;
              }
            }}
            disabled={!state || (state.status !== 'complete' && state.status !== 'failed')}
            className={cn(
              'font-medium transition-all duration-200 shadow-sm',
              pinViewTabsAbovePic
                ? 'border-b-2 rounded-b-none rounded-t-md'
                : 'border-b-2 rounded-t-none rounded-b-md',
              'h-11 min-h-11 min-w-0 px-1 text-xs leading-tight',
              statusStyles[statusClass],
              currentView === view ? 'border-primary brightness-110' : 'border-transparent',
              'disabled:opacity-70',
            )}
          >
            {text}
          </Button>
        );
      })}
    </div>
  );

  /*
   * S3b — docked capture chrome.
   *
   * The shell is a flex column: info bar, media area, action strip. It owns the *width*; the media
   * child owns the *height*, so docking the bars grows the card rather than silently shrinking the
   * frame's width too (the frame is aspect-locked, so height-only is the cheaper trade).
   *
   * In native fullscreen the shell is forced to fill the screen by the UA stylesheet, so the media
   * child drops its explicit height and takes the leftover space via `flex-1` instead.
   */
  const dockedMediaHeightPx =
    canvasSize.height > 0
      ? isCameraActive && liveStreamAspect && canvasSize.width > 0
        ? Math.min(canvasSize.height, Math.round(canvasSize.width / liveStreamAspect))
        : canvasSize.height
      : undefined;
  const dockStripAtTop = captureSettings.toolbarPosition === 'top';
  const showGuidedInfoBar = captureMode === 'guided' && isCameraActive;
  /**
   * The labelled overflow is for the *editing* state, which can have ten tools live at once. While
   * the camera is running there are at most four controls, so they all stay visible — no menu to
   * open one-handed with a client mid-pose.
   */
  const collapseSecondaryTools = isMobile && !isCameraActive;
  /** The docked strip has horizontal room the floating pill did not, so desktop gets text too. */
  const showPrimaryLabels = (laymanMode && !laymanToolbarCompact) || !isMobile;
  const primaryCameraLabel = laymanMode
    ? laymanCameraLabel
    : isCameraActive
      ? (nextPendingCaptureView ? 'Capture' : 'Finish capture')
      : 'Start Camera';
  /**
   * The setup reminder used to live in the wizard on step 1, on top of a live feed — too late to act
   * on, since the phone is already propped by then. Here it lands in the one moment it is actionable.
   */
  const showPreCaptureSetupHint =
    !isCameraActive && !hasAnyCapturedImage && !isSelfAssessmentEmptyDropZone && showPractitionerStrip;

  const captureInfoBar = showGuidedInfoBar ? (
    <GuidedCaptureWizard
      currentStep={currentStepIndex >= 0 ? currentStepIndex : 0}
      totalSteps={effectiveSequence.length}
      currentView={nextView as CaptureSequence}
      onStepChange={() => { }} // Steps are auto-advanced by capture
      onComplete={() => stopCamera()}
      isCaptureComplete={isCaptureComplete}
      messageOverrides={mode.wizardOverrides}
      immersiveMobile={isMobile}
      feedback={feedback}
      onCloseCamera={handleCloseCameraView}
    />
  ) : showPreCaptureSetupHint ? (
    <div
      data-testid="capture-setup-hint-bar"
      className="flex shrink-0 items-start gap-2 border-b border-line bg-surface/90 px-3 py-1.5 backdrop-blur-md"
    >
      <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      <p className="min-w-0 text-[11px] font-medium leading-snug text-fg">
        {CAPTURE_SETUP_WIZARD_REMINDER}
      </p>
    </div>
  ) : null;

  return (
    <div className={cn(
      'flex flex-col',
      fillAvailable ? 'h-full min-h-0 flex-1 gap-1' : compactChrome ? 'gap-1.5' : 'gap-4',
      isSelfAssessment && 'light',
    )}>
      <input type="file" ref={uploadInputRef} multiple accept="image/*" className="hidden" onChange={handleUploadChange} />

      {/* Assign Uploaded Images - shown above the viewer */}
      {unassignedImages.length > 0 && (
        <Card>
          <CardHeader className="flex flex-row items-start justify-between gap-3 space-y-0 pb-3">
            <CardTitle className="text-base leading-snug">
              {laymanMode ? 'Assign your photos' : 'Assign Uploaded Images'}
            </CardTitle>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0 text-muted-foreground hover:text-foreground"
              aria-label="Close uploaded images"
              onClick={() => setUnassignedImages([])}
            >
              <X className="h-4 w-4" />
            </Button>
          </CardHeader>
          <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-0">
            {unassignedImages.map(img => (
              <div key={img.id} className="relative border border-border rounded-lg p-2 flex flex-col gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="absolute right-1 top-1 z-10 h-7 w-7 rounded-full bg-background/80 text-muted-foreground hover:text-foreground shadow-sm"
                  aria-label="Remove uploaded photo"
                  onClick={() => setUnassignedImages(prev => prev.filter(uImg => uImg.id !== img.id))}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
                <FullScreenImageDialog imageUrl={img.dataUrl} altText="Uploaded Image">
                  <img src={img.dataUrl} alt="Uploaded" className="w-full h-24 object-contain mb-2 rounded cursor-pointer" />
                </FullScreenImageDialog>
                <Select
                  onValueChange={(selectedView: CaptureSequence) => {
                    if (selectedView) {
                      onQueueImage(img.dataUrl, selectedView);
                      setUnassignedImages(prev => prev.filter(uImg => uImg.id !== img.id));
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={laymanMode ? 'Choose view' : 'Assign View'} />
                  </SelectTrigger>
                  <SelectContent>
                    {effectiveSequence.map(view => (
                      <SelectItem key={view} value={view} disabled={!!capturedImages[view]}>
                        {viewDisplayLabel(view)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {pinViewTabsAbovePic && viewSelectorGrid}

      {/* Viewer - wrapped in Card for consistent border */}
      <div
        ref={fillFrameRef}
        className={cn(fillAvailable && 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden')}
      >
      <Card
        className={cn(
          'overflow-hidden relative',
          preferFullWidthMobileCanvas && 'w-full',
          fillAvailable && 'flex h-full min-h-0 w-full max-w-full flex-col',
          !isSelfAssessment && !fillAvailable && 'overscroll-contain',
          isSelfAssessment && isMobile && 'touch-pan-y',
          captureCardClass,
        )}
      >
        <CardContent className={cn(
          'p-0',
          fillAvailable && 'flex h-full min-h-0 flex-1 flex-col',
        )}>
          {/*
            S3b — the capture "sandwich": docked info bar, unobstructed media, docked action strip.
            This shell (not the media box) is the native-fullscreen target, because chrome docked
            outside the fullscreen element would disappear the moment the camera opens on a phone.
          */}
          <div
            ref={cameraViewportRef}
            data-testid="capture-viewport-shell"
            className={cn(
              'relative flex flex-col',
              fillAvailable && 'h-full min-h-0 w-full flex-1',
              captureViewportClass,
              isSelfAssessment && isMobile && 'touch-pan-y',
              isSelfAssessmentEmptyDropZone
                ? 'w-full'
                : preferFullWidthMobileCanvas
                  ? 'w-full max-w-full'
                  : canvasSize.width > 0
                    ? ''
                    : 'w-full lg:w-1/2',
              !preferFullWidthMobileCanvas && !isSelfAssessmentEmptyDropZone && 'mx-auto',
            )}
            /*
              Merge note (master 373261b8): master sized this element as the media box itself —
              explicit height, `aspect-[3/4]`, and a hard `width: <canvasSize>px`. Since S3b this
              element is the *shell* that stacks info bar + media + action strip, so a height here
              would stretch the chrome rather than the photo. Height now belongs to the media child
              (`dockedMediaHeightPx`, which already reflects master's `currentImageAspectRatio`
              because that feeds `useCanvasSize`).

              What is kept from master is the width strategy: percentage width with a desktop-only
              max, so a wide capture can no longer overflow a narrow viewport.
            */
            style={
              fillAvailable || isSelfAssessmentEmptyDropZone || preferFullWidthMobileCanvas
                ? { width: '100%' }
                : canvasSize.width > 0
                  ? { width: '100%', maxWidth: isMobile ? '100%' : `${canvasSize.width}px` }
                  : undefined
            }
          >
            {/*
              DOM order is always info → media → strip, which is also the right reading order for a
              screen reader. `toolbarPosition` only moves the strip *visually*, via flex order.
            */}
            {captureInfoBar}

            {/*
              Desktop: the picture fits the space above the docked controls.
              A larger window gives that box more room; a smaller window gives it less.
              The whole frame stays visible, so the picture itself does not scroll.
            */}
            <div
              data-testid="capture-media-scroll"
              className={cn(
                fitDesktopPhoto
                  ? 'order-2 flex min-h-0 flex-1 items-center justify-center overflow-hidden'
                  : 'contents',
              )}
            >
            <div
              data-testid="capture-media-area"
              className={cn(
                'relative flex w-full items-center justify-center',
                fitDesktopPhoto
                  ? 'h-full max-h-full min-h-0'
                  : 'order-2',
                !fitDesktopPhoto && (isNativeFullscreen
                  ? 'min-h-0 flex-1'
                  : isSelfAssessmentEmptyDropZone
                    ? ''
                    : dockedMediaHeightPx === undefined
                      ? 'aspect-[3/4]'
                      : ''),
                /*
                  3:4 is the *fallback* framing, not a rule. Once a photo exists we know its real
                  ratio (master 373261b8 threads it through `useCanvasSize`), so forcing 3:4 here
                  would letterbox a capture we can size exactly.
                */
                preferFullWidthMobileCanvas && !isNativeFullscreen && !hasAnyCapturedImage && 'aspect-[3/4]',
              )}
              style={
                isNativeFullscreen || fitDesktopPhoto
                  ? undefined
                  : fullWidthPhotoHeightPx > 0
                    ? { height: `${fullWidthPhotoHeightPx}px` }
                    : isSelfAssessmentEmptyDropZone
                      ? { height: `${selfAssessmentEmptyDropZoneHeightPx}px` }
                      : preferFullWidthMobileCanvas && inlineMaxCanvasHeightPx && !hasAnyCapturedImage
                        ? { maxHeight: `${inlineMaxCanvasHeightPx}px` }
                        : dockedMediaHeightPx !== undefined
                          ? { height: `${dockedMediaHeightPx}px` }
                          : undefined
              }
            >
            <video
              ref={videoRef}
              autoPlay
              playsInline
              onLoadedMetadata={(e) => {
                const el = e.currentTarget;
                if (el.videoWidth > 0 && el.videoHeight > 0) {
                  setLiveStreamAspect(el.videoWidth / el.videoHeight);
                }
              }}
              className={`absolute inset-0 h-full max-h-full w-full min-h-0 min-w-0 object-contain ${isCameraActive ? 'block' : 'hidden'}`}
              style={{
                filter: getEnhancementCSS(captureSettings),
                transform: getTransformCSS(captureSettings),
              }}
            ></video>
            <canvas
              ref={canvasRef}
              onClick={handleCanvasClick}
              onPointerDown={handleCanvasPointerDown}
              onPointerMove={handleCanvasPointerMove}
              onPointerUp={handleCanvasPointerUp}
              onPointerLeave={handleCanvasPointerLeave}
              onPointerCancel={handleCanvasPointerUp}
              className={`absolute inset-0 h-full max-h-full w-full min-h-0 min-w-0 object-contain ${!isCameraActive ? 'block' : 'hidden'}`}
              style={{
                ...(inlineCanvasTouchAction ? { touchAction: inlineCanvasTouchAction } : {}),
                ...((hoverLandmarkIndexForDraw !== null || hoverLineSegmentForDraw !== null) && onSetPoseLandmark ? { cursor: 'grab' } : {}),
              }}
            ></canvas>

            {/* Smart Overlay Layer */}
            {isCameraActive && (
              <CaptureOverlay
                currentView={nextView as CaptureSequence}
                landmarks={realtimeLandmarks}
                isStable={isStable}
                showGrid={captureSettings.gridType !== 'none'}
                showGhost={captureSettings.showGhostOutline}
                showLevel={captureSettings.showLevelIndicator}
                showPlumbLine={captureSettings.showPlumbLine}
                gridType={captureSettings.gridType}
                gridOpacity={captureSettings.gridOpacity}
                ghostOpacity={captureSettings.ghostOpacity}
                plumbLineStyle={captureSettings.plumbLineStyle}
                liveFloorCorrectionDeg={liveFloorCorrectionDeg}
                liveGyroTilt={deviceTilt}
                liveGyroSupported={deviceOrientationSupported}
              />
            )}

            {/*
              Camera failures happen while the camera is *inactive*, so they can never
              be shown by the docked info bar above (it mounts only while active).
              This surface guarantees the error is seen and offers a way out.
            */}
            {!isCameraActive && feedback?.type === 'error' && (
              <CameraErrorNotice
                message={feedback.message}
                onRetry={() => { void startCamera(); }}
                onUploadInstead={() => {
                  setFeedback(null);
                  handleUploadClick();
                }}
                onDismiss={() => setFeedback(null)}
              />
            )}

            {/* Real-time Landmark Overlay Canvas */}
            {isCameraActive && (
              <RealtimeOverlayCanvas
                isActive={isCameraActive && overlayState[nextView as CaptureSequence]?.realtime === true}
                landmarks={realtimeLandmarks}
                currentView={nextView as CaptureSequence}
                captureSettings={captureSettings}
                videoElement={videoRef.current}
                liveFloorCorrectionDeg={liveFloorCorrectionDeg}
              />
            )}

            {/* Countdown Layer */}
            <CaptureCountdown
              isActive={isCountdownActive}
              onComplete={handleCountdownComplete}
              onCancel={handleCountdownCancel}
              startCount={captureSettings.countdownDuration}
            />

            {/* Classification Overlay */}
            <PostureClassificationOverlay
              classification={classification || null}
              isVisible={!!showClassificationOverlay}
              onClose={() => onToggleClassificationOverlay?.()}
            />
            </div>
            </div>

            {/*
              S3b — docked action strip. This was a floating pill at `absolute bottom-4`, i.e. over
              the subject's feet and ankles; it is now in-flow chrome below (or above) the frame.
            */}
            {showPractitionerStrip && (
              <div
                className={cn(
                  'flex min-w-0 shrink-0 justify-center',
                  dockStripAtTop ? 'order-1' : 'order-3',
                )}
              >
              <TooltipProvider>
                <div
                  className={cn(
                    captureToolbarShellClass,
                    'w-full max-w-full overflow-hidden',
                    dockStripAtTop ? 'border-b' : 'border-t',
                  )}
                >
                  {visibleCenterMessage && (
                    <div className="px-3 py-1 text-xs font-medium text-center border-b border-line bg-surface/50 text-fg whitespace-nowrap overflow-hidden text-ellipsis">
                      {visibleCenterMessage}
                    </div>
                  )}
                  {/*
                    S3/U1: `scrollbar-hide` removed. Controls must never scroll silently out of
                    reach — the row wraps visibly if it cannot fit. S3b makes the wrap almost
                    unreachable: the live-camera state now carries four controls, not ten.
                  */}
                  <div className="flex min-w-0 max-w-full flex-wrap items-center justify-center gap-1 px-2 py-1.5 [&>*]:shrink-0 [@media(max-width:767px)_and_(max-height:740px)]:gap-0.5 [@media(max-width:767px)_and_(max-height:740px)]:px-1 [@media(max-width:767px)_and_(max-height:740px)]:py-0.5">
                  {/* Secondary tools — inline in their original order; collapsed only while editing on a phone */}
                  {!collapseSecondaryTools && (
                    <CaptureToolbarSecondary
                      actions={leadingSecondaryActions}
                      collapsed={false}
                      buttonClass={captureToolbarBtnClass}
                    />
                  )}

                  {/* Vertical Separator — only meaningful with tools on its left and upload on its right */}
                  {!collapseSecondaryTools && leadingSecondaryActions.length > 0 && !isCameraActive && (
                    <div className={cn('w-px h-4 mx-0.5 self-center', captureToolbarSepClass)} />
                  )}

                  {/*
                    Upload leaves the live-camera strip (S3b): you are framing a shot, not picking a
                    file, and dropping it is what keeps the live state down to four controls. Stop the
                    camera — the close button in the info bar — and it comes back.
                  */}
                  {!isCameraActive && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={() => uploadInputRef.current?.click()}
                        disabled={!isReadyForAnalysis}
                        variant="ghost"
                        size={showPrimaryLabels ? 'sm' : 'icon'}
                        className={cn(
                          showPrimaryLabels
                            ? 'h-11 px-4 rounded-full gap-1.5 text-xs font-medium'
                            : cn(CAPTURE_TOOLBAR_TARGET_CLASS, 'rounded-full'),
                          captureToolbarBtnClass,
                        )}
                        aria-label={laymanMode ? laymanUploadLabel : 'Upload'}
                      >
                        <UploadIcon className={cn(CAPTURE_TOOLBAR_ICON_CLASS, 'shrink-0')} />
                        {showPrimaryLabels && <span>{laymanUploadLabel}</span>}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Upload or paste image (Ctrl+V / Cmd+V)</p>
                    </TooltipContent>
                  </Tooltip>
                  )}

                  {/* Camera-only secondary tools — kept left of the shutter */}
                  {!collapseSecondaryTools && (
                    <CaptureToolbarSecondary
                      actions={cameraSecondaryActions}
                      collapsed={false}
                      buttonClass={captureToolbarBtnClass}
                    />
                  )}

                  {/* Capture Button - CENTER */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        onClick={handleCaptureClick}
                        disabled={!isReadyForAnalysis || (isCameraActive && !nextPendingCaptureView)}
                        size={showPrimaryLabels ? 'sm' : 'icon'}
                        className={cn(
                          showPrimaryLabels
                            ? 'h-11 px-4 rounded-full gap-1.5 text-xs font-medium shadow-md'
                            : cn(CAPTURE_TOOLBAR_TARGET_CLASS, 'rounded-full shadow-md'),
                          isCameraActive
                            ? 'bg-red-600 hover:bg-red-500 text-white'
                            : captureStartCameraClass,
                        )}
                        aria-label={
                          laymanMode
                            ? laymanCameraLabel
                            : isCameraActive
                              ? (nextPendingCaptureView ? 'Capture' : 'Finish capture')
                              : 'Start Camera'
                        }
                      >
                        <CameraIcon
                          active={isCameraActive}
                          className={cn('shrink-0', laymanMode ? 'h-4 w-4' : 'h-4.5 w-4.5')}
                        />
                        {showPrimaryLabels && <span>{primaryCameraLabel}</span>}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        {isCameraActive
                          ? (nextPendingCaptureView ? `Snap ${viewDisplayLabel(nextPendingCaptureView)}` : 'Finish capture')
                          : laymanMode
                            ? laymanCameraLabel
                            : 'Start Camera'}
                      </p>
                    </TooltipContent>
                  </Tooltip>

                  {/* Trailing secondary tools — kept right of the shutter */}
                  {!collapseSecondaryTools && (
                    <CaptureToolbarSecondary
                      actions={trailingSecondaryActions}
                      collapsed={false}
                      buttonClass={captureToolbarBtnClass}
                    />
                  )}

                  {/* Settings Button - Always visible when camera active or image captured */}
                  {(isCameraActive || (currentView && capturedImages[currentView])) && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          onClick={() => setIsSettingsOpen(true)}
                          variant="ghost"
                          size="icon"
                          className={cn(CAPTURE_TOOLBAR_TARGET_CLASS, 'rounded-full', captureToolbarBtnClass)}
                          aria-label="Settings"
                        >
                          <SettingsIcon className={CAPTURE_TOOLBAR_ICON_CLASS} />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Settings</p>
                      </TooltipContent>
                    </Tooltip>
                  )}

                  {/*
                    Editing on a phone: every secondary tool in one labelled overflow, in the same
                    order the desktop strip uses. Placed last so the primaries keep the centre. The
                    live-camera state never gets here — it has few enough controls to show them all.
                  */}
                  {collapseSecondaryTools && (
                    <CaptureToolbarSecondary
                      actions={allSecondaryActions}
                      collapsed
                      buttonClass={captureToolbarBtnClass}
                    />
                  )}
                  </div>
                </div>
              </TooltipProvider>
            </div>
            )}
          </div>
        </CardContent>
      </Card>
      </div>

      {!pinViewTabsAbovePic && viewSelectorGrid}

      {isSelfAssessment && currentView && !isCaptureComplete && (
        <div className="w-full mt-2 font-['Roboto',sans-serif]">
          <button
            type="button"
            onClick={() => {
              const nextState = !showConorExample;
              setShowConorExample(nextState);
              if (nextState) {
                setTimeout(() => {
                  document.getElementById('conor-example-drawer')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                }, 100);
              }
            }}
            className="w-full py-2 px-3 flex items-center justify-between border border-line bg-surface rounded-xl hover:bg-bg transition-colors focus-visible:outline-none"
            aria-expanded={showConorExample}
          >
            <div className="flex items-center gap-2">
              <span className="text-xs font-bold text-fg">
                📸 See Conor's example picture for {viewDisplayLabel(currentView)}
              </span>
            </div>
            {showConorExample ? (
              <ChevronUp className="h-4 w-4 text-muted" />
            ) : (
              <ChevronDown className="h-4 w-4 text-muted" />
            )}
          </button>
          {showConorExample && (
            <div id="conor-example-drawer" className="mt-2 p-3 bg-surface border border-line rounded-xl flex flex-col items-center gap-2 animate-in fade-in duration-200">
              <img
                src={
                  currentView === 'Anterior'
                    ? '/self-assessment/conor-front.jpeg'
                    : currentView === 'Left'
                    ? '/self-assessment/conor-left.jpeg'
                    : '/self-assessment/conor-right.jpeg'
                }
                alt={`Conor Harris ${viewDisplayLabel(currentView)} Example`}
                className="max-w-[200px] w-full h-auto rounded-lg border border-line shadow-sm object-contain"
              />
              <span className="text-[10px] font-bold text-muted text-center">
                Example {viewDisplayLabel(currentView)} Stance
              </span>
            </div>
          )}
        </div>
      )}

      {/* Mode Switch - Minimal text link below */}
      {onCaptureModeChange && (
        <div className="flex justify-end px-1">
          <Button
            variant="link"
            size="sm"
            className="h-auto p-0 text-xs text-muted-foreground"
            onClick={() => onCaptureModeChange(captureMode === 'guided' ? 'manual' : 'guided')}
          >
            {captureMode === 'guided' ? 'Switch to Manual Mode' : 'Switch to Guided Mode'}
          </Button>
        </div>
      )}

      {/* Settings Panel */}
      <CaptureSettingsPanel
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        settings={captureSettings}
        onSettingsChange={onCaptureSettingsChange}
        onReanalyze={onReanalyze}
        plumbToolState={{
          source: currentImage?.floorCorrectionSource,
          confidence: currentImage?.floorCorrectionConfidence,
          warning: currentImage?.floorCorrectionWarning,
        }}
        onStartManualPlumbAdjust={onManualPlumbUpdate && currentView ? () => {
          clearPlumbAutoExitTimer();
          setIsManualPlumbMode(true);
          setVisibleCenterMessage('PL edit: drag top/bottom handles');
          schedulePlumbAutoExit();
        } : undefined}
        onResetPlumbToAuto={onManualPlumbUpdate && currentView ? () => {
          onManualPlumbUpdate(currentView, { resetToAuto: true });
          clearPlumbAutoExitTimer();
          setIsManualPlumbMode(false);
          setVisibleCenterMessage('Plumb reset to auto');
        } : undefined}
      />

      <FullScreenImageDialog
        open={isFullScreenOpen}
        immersive={immersiveFullscreen}
        onOpenChange={(open) => {
          setIsFullScreenOpen(open);
          if (!open) {
            applyFullscreenViewport(1, { x: 0, y: 0 }, fullscreenCanvasRef.current);
            setShowFullscreenMagnifier(false);
            setFullscreenMagnifierImagePoint(null);
            setActiveDragPoseIndex(null);
            setActiveDragPinKey(null);
            setIsPanningCanvas(false);
            panDragRef.current = null;
            fullscreenPinchStartRef.current = null;
            fullscreenTouchPointsRef.current.clear();
            setZoomPanelOffset({ x: 0, y: 0 });
          }
        }}
        dialogContent={(
          <div className="flex h-full w-full min-h-0 min-w-0 flex-col gap-2 bg-bg sm:flex-row">
            <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-md bg-bg">
            <canvas
              ref={fullscreenCanvasRef}
              onClick={handleCanvasClick}
              onPointerDown={handleCanvasPointerDown}
              onPointerMove={handleCanvasPointerMove}
              onPointerUp={handleCanvasPointerUp}
              onPointerLeave={handleCanvasPointerLeave}
              onPointerCancel={handleCanvasPointerUp}
              onWheel={handleFullscreenWheel}
              className="w-full h-full object-contain block"
              style={{
                touchAction: 'none' as const,
                ...(isPanningCanvas
                  ? { cursor: 'grabbing' as const }
                  : (
                    fullscreenZoom > 1 ||
                    ((hoverLandmarkIndexForDraw !== null || hoverLineSegmentForDraw !== null || hoverPelvisPinKeyForDraw !== null || hoverPelvisLineForDraw) && onSetPoseLandmark)
                  )
                    ? { cursor: 'grab' as const }
                    : {}),
              }}
            />
            <div
              className={cn(
                'absolute z-20 flex flex-col pointer-events-auto max-w-[min(100vw-1.5rem,20rem)]',
                isMobile
                  ? 'top-[max(0.5rem,env(safe-area-inset-top))] left-[max(0.5rem,env(safe-area-inset-left))] gap-2.5'
                  : 'top-[max(0.75rem,env(safe-area-inset-top))] left-[max(0.75rem,env(safe-area-inset-left))] gap-2'
              )}
            >
              {currentView && (
                <div
                  className={cn(
                    'flex items-center rounded-md',
                    fullscreenChromeClass,
                    isMobile ? 'gap-2 p-1.5' : 'gap-1 p-1'
                  )}
                >
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className={cn(
                      fullscreenChromeBtnClass,
                      isMobile ? 'h-10 w-10 min-h-10 min-w-10' : 'h-8 w-8'
                    )}
                    aria-label="Previous captured view"
                    disabled={completeViewsForFullscreen.length < 2}
                    onClick={() => cycleFullscreenView(-1)}
                  >
                    <ChevronLeft className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
                  </Button>
                  {!isMobile && (
                    <Select
                      value={currentView}
                      onValueChange={(v) => {
                        stopCamera();
                        onViewSelect(v as CaptureSequence);
                      }}
                    >
                      <SelectTrigger
                        aria-label="Select posture view"
                        className="h-8 min-w-[5.5rem] max-w-[9rem] border-line bg-bg text-xs text-fg"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {effectiveSequence.map((view) => {
                          const st = capturedImages[view]?.status;
                          return (
                            <SelectItem key={view} value={view} disabled={st !== 'complete'}>
                              {viewDisplayLabel(view)}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  )}
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className={cn(
                      fullscreenChromeBtnClass,
                      isMobile ? 'h-10 w-10 min-h-10 min-w-10' : 'h-8 w-8'
                    )}
                    aria-label="Next captured view"
                    disabled={completeViewsForFullscreen.length < 2}
                    onClick={() => cycleFullscreenView(1)}
                  >
                    <ChevronRight className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
                  </Button>
                </div>
              )}
              {isMobile && currentView && (
                <p className={cn('rounded-md px-2 py-1 text-center text-[11px] font-medium', fullscreenChromeClass)}>
                  {viewDisplayLabel(currentView)}
                </p>
              )}
              {!isMobile && showAnalysisDashboardToggle && onToggleAnalysisPanel && (
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  className={cn(fullscreenChromeBtnClass, 'justify-start')}
                  aria-pressed={analysisPanelEnabled}
                  aria-label={
                    analysisPanelEnabled ? 'Hide metrics dashboard (View Report)' : 'Show metrics dashboard (View Report)'
                  }
                  onClick={onToggleAnalysisPanel}
                >
                  <LayoutList className="h-4 w-4 mr-2 shrink-0" />
                  {analysisPanelEnabled ? 'Metrics on' : 'Metrics off'}
                </Button>
              )}
              {isMobile && showAnalysisDashboardToggle && onToggleAnalysisPanel && (
                <Button
                  type="button"
                  variant="secondary"
                  size="icon"
                  className={cn(fullscreenChromeBtnClass, 'h-10 w-10 min-h-10 min-w-10')}
                  aria-pressed={analysisPanelEnabled}
                  aria-label={
                    analysisPanelEnabled
                      ? (laymanMode ? 'Hide numbers' : 'Hide metrics dashboard (View Report)')
                      : (laymanMode ? 'Show numbers' : 'Show metrics dashboard (View Report)')
                  }
                  onClick={onToggleAnalysisPanel}
                >
                  <LayoutList className="h-5 w-5 shrink-0" />
                </Button>
              )}
            </div>
            <div
              className={cn(
                'absolute z-20 flex flex-wrap items-center justify-end',
                isMobile
                  ? 'top-[max(0.5rem,env(safe-area-inset-top))] right-[max(0.5rem,env(safe-area-inset-right))] gap-2.5'
                  : 'top-[max(0.75rem,env(safe-area-inset-top))] right-[max(0.75rem,env(safe-area-inset-right))] gap-2'
              )}
            >
              {fullscreenEditHistory && (
                <>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className={cn(
                      fullscreenChromeBtnClass,
                      isMobile && 'h-10 w-10 min-h-10 min-w-10'
                    )}
                    aria-label="Undo last edit"
                    disabled={!fullscreenEditHistory.canUndo}
                    onClick={() => fullscreenEditHistory.onUndo()}
                  >
                    <Undo2 className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
                  </Button>
                  <Button
                    type="button"
                    variant="secondary"
                    size="icon"
                    className={cn(
                      fullscreenChromeBtnClass,
                      isMobile && 'h-10 w-10 min-h-10 min-w-10'
                    )}
                    aria-label="Redo edit"
                    disabled={!fullscreenEditHistory.canRedo}
                    onClick={() => fullscreenEditHistory.onRedo()}
                  >
                    <Redo2 className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
                  </Button>
                </>
              )}
              <Button
                type="button"
                variant="secondary"
                size={isMobile ? 'icon' : 'sm'}
                aria-pressed={isFullscreenMagnifierEnabled}
                aria-label={isFullscreenMagnifierEnabled ? 'Disable magnifier' : 'Enable magnifier'}
                className={cn(
                  'shrink-0',
                  isMobile && 'h-10 w-10 min-h-10 min-w-10',
                  isFullscreenMagnifierEnabled
                    ? 'border border-primary bg-primary/20 text-fg ring-2 ring-primary hover:bg-primary/30'
                    : fullscreenChromeBtnClass
                )}
                onClick={() => {
                  setIsFullscreenMagnifierEnabled((prev) => !prev);
                  setShowFullscreenMagnifier(false);
                }}
              >
                {isMobile ? (
                  isFullscreenMagnifierEnabled ? (
                    <ZoomOut className="h-5 w-5" />
                  ) : (
                    <ZoomIn className="h-5 w-5" />
                  )
                ) : isFullscreenMagnifierEnabled ? (
                  <>
                    <ZoomOut className="h-4 w-4 mr-1" />
                    Magnifier On
                  </>
                ) : (
                  <>
                    <ZoomIn className="h-4 w-4 mr-1" />
                    Magnifier Off
                  </>
                )}
              </Button>
              <Button
                type="button"
                variant="secondary"
                size={isMobile ? 'icon' : 'sm'}
                className={cn(
                  fullscreenChromeBtnClass,
                  isMobile && 'h-10 w-10 min-h-10 min-w-10'
                )}
                aria-label="Close fullscreen"
                onClick={() => setIsFullScreenOpen(false)}
              >
                {isMobile ? <X className="h-5 w-5" /> : 'Close'}
              </Button>
            </div>
            {isFullscreenMagnifierEnabled && showFullscreenMagnifier && (
              <div className="absolute left-[max(0.75rem,env(safe-area-inset-left))] bottom-[max(0.75rem,env(safe-area-inset-bottom))] z-20">
                {activeFullscreenLandmarkLabel && (
                  <div className={cn('mb-2 inline-flex rounded px-2 py-1 text-xs font-medium', fullscreenChromeClass)}>
                    {activeFullscreenLandmarkLabel}
                  </div>
                )}
                <div className="overflow-hidden rounded-md border border-primary bg-surface shadow-lg">
                <canvas
                  ref={fullscreenMagnifierCanvasRef}
                  width={160}
                  height={160}
                  className="block w-40 h-40"
                  aria-label="Magnified image (no overlays)"
                />
                </div>
              </div>
            )}
            <div
              className={cn(
                'absolute z-20 flex select-none touch-none rounded-md',
                fullscreenChromeClass,
                isMobile
                  ? 'bottom-[max(0.5rem,env(safe-area-inset-bottom))] right-2 flex-col items-center gap-1 px-1 py-2'
                  : 'bottom-[max(0.75rem,env(safe-area-inset-bottom))] right-3 items-center gap-2 px-2 py-1'
              )}
              style={{
                transform: `translate(${zoomPanelOffset.x}px, ${zoomPanelOffset.y}px)`,
                cursor: isDraggingZoomPanel ? 'grabbing' : 'grab',
              }}
              onPointerDown={handleZoomPanelPointerDown}
              onPointerMove={handleZoomPanelPointerMove}
              onPointerUp={handleZoomPanelPointerUp}
              onPointerCancel={handleZoomPanelPointerUp}
            >
              {isMobile ? (
                <GripHorizontal className="h-4 w-4 shrink-0 cursor-grab text-muted active:cursor-grabbing" />
              ) : (
                <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-muted active:cursor-grabbing" />
              )}
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className={cn(
                  fullscreenChromeBtnClass,
                  isMobile ? 'h-10 w-10 min-h-10 min-w-10 order-3' : 'order-1'
                )}
                aria-label="Zoom out fullscreen image"
                onClick={() => stepFullscreenZoom(-1)}
              >
                <Minus className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              </Button>
              <div
                className={cn(
                  'text-center font-medium text-fg tabular-nums',
                  isMobile ? 'min-w-[2.75rem] text-[11px] order-2' : 'min-w-[3rem] text-xs order-2'
                )}
              >
                {Math.round(fullscreenZoom * 100)}%
              </div>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className={cn(
                  fullscreenChromeBtnClass,
                  isMobile ? 'h-10 w-10 min-h-10 min-w-10 order-1' : 'order-3'
                )}
                aria-label="Zoom in fullscreen image"
                onClick={() => stepFullscreenZoom(1)}
              >
                <Plus className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="icon"
                className={cn(
                  fullscreenChromeBtnClass,
                  isMobile ? 'h-10 w-10 min-h-10 min-w-10 order-4' : 'order-4'
                )}
                aria-label="Reset zoom and pan to 100%"
                title="Reset zoom"
                disabled={
                  fullscreenZoom === 1 && fullscreenPan.x === 0 && fullscreenPan.y === 0
                }
                onClick={() => {
                  applyFullscreenViewport(1, { x: 0, y: 0 }, fullscreenCanvasRef.current);
                  setZoomPanelOffset({ x: 0, y: 0 });
                }}
              >
                <RotateCcw className={isMobile ? 'h-5 w-5' : 'h-4 w-4'} />
              </Button>
            </div>
            </div>
            {analysisPanelEnabled && showAnalysisDashboardToggle && (
              <div
                className="order-last mb-[env(safe-area-inset-bottom,0px)] flex max-h-[42vh] w-full min-h-0 shrink-0 flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-xl sm:order-none sm:mb-0 sm:mr-[env(safe-area-inset-right,0px)] sm:max-h-none sm:w-[min(380px,38vw)] sm:max-w-[450px]"
                aria-label="View report metrics"
              >
                <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden p-2">
                  <AnalysisTiles
                    classification={classification ?? null}
                    analysisData={analysisData}
                    model3D={model3D}
                    capturedImages={capturedImages}
                    settings={settings}
                    currentView={currentView}
                    experimentalFeaturesEnabled={experimentalFeaturesEnabled === true}
                    defaultExpanded
                    pelvisLineColor={captureSettings.pelvisLineColor}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        altText="Captured Posture Analysis"
      />
    </div>
  );
};

export default CaptureModule;
